// 🕹️ MikoArcadeCanvas — Level 1 playable beat-em-up (Feb 2026)
//
// Canonical scope (owner-locked):
//   • Character: Deanah & Muki (they saw Miko through this exact cabinet)
//   • Level: Area 1 · The Scrap Yards (1-1 Perimeter Gates → 1-2 Conveyor Belts
//     → 1-3 mini-boss Junkyard Crusher 9000)
//   • Bright Scott-Pilgrim palette, cartoon impact bubbles, neon specials.
//   • PC keyboard (WASD + JKL) AND mobile touch (D-pad + 3 buttons).
//   • Muki stays "pet only" on Bars 1 & 2, joins on the Bar 3 Meteor Drop
//     (per Aug 2026 owner canon).
//
// The canvas is a self-contained 60fps game module. It receives the
// backend `session` payload and calls `onExit(result)` when the run
// resolves (victory or GAME OVER). Rewards are settled by the parent
// via POST /api/miko-arcade/session/end.

import { useEffect, useRef, useState } from "react";
import { Loader2, Volume2, VolumeX } from "lucide-react";
import { ARCADE_MUSIC } from "@/lib/arcadeMusic";

// ── Palette (bright, saturated, cartoon-arcade) ─────────────────────
const P = {
  skyTop:    "#38BDF8",
  skyBottom: "#F0ABFC",
  ground:    "#78350F",
  scrap:     "#B45309",
  scrap2:    "#F59E0B",
  deanah:    "#EC4899",
  muki:      "#F0ABFC",
  gruntA:    "#A855F7",
  gruntB:    "#7C3AED",
  boss:      "#EF4444",
  // Area 2 · Automated Assembly Line palette (Feb 9, 2026)
  droidA:    "#94A3B8",   // chrome line-worker droid
  droidB:    "#64748B",
  foreman:   "#334155",   // heavier iron chassis
  spider:    "#0F172A",   // ceiling-drop spider
  factory:   "#1F2937",   // steel decking
  factory2:  "#334155",
  hp:        "#22C55E",
  hpBad:     "#EF4444",
  meter:     "#FACC15",
  outline:   "#0B0F19",
  bubble1:   "#F472B6",  // POW
  bubble2:   "#38BDF8",  // SMACK
  bubble3:   "#FACC15",  // K.O.
  bubble4:   "#4ADE80",  // BARF
  flashA:    "#F0ABFC",
  flashB:    "#22D3EE",
};

// ── Level layout ────────────────────────────────────────────────────
const STAGE_WIDTH = 6400;           // scrollable world width (Feb 9: doubled for Area 2)
const CANVAS_W = 640;
const CANVAS_H = 360;
const GROUND_Y = 300;

// 🗺️ ZONES — Feb 9, 2026 pivot: split the 3200-wide corridor into
// visibly separate combat and safe rooms. Every enemy is tagged with
// its zone and clamped to its bounds so backtracking into a cleared
// safe zone doesn't drag the mob with you. Zone banners fire once on
// first crossing. Owner directive: give every enemy attire matching
// its zone (Area 1 · Stage 1 = The Scrap Yards → greasy jumpsuits).
const ZONES = [
  // ── AREA 1 · The Scrap Yards ─────────────────────────────────
  { id: "z1_gates",    type: "combat", x0:    0, x1:  820, label: "SCRAP GATES",        floor: "rust",    theme: "scrap",   area: 1 },
  { id: "z2_cache",    type: "safe",   x0:  820, x1: 1180, label: "SUPPLY CACHE",       floor: "gravel",  theme: "scrap",   area: 1 },
  { id: "z3_belts",    type: "combat", x0: 1180, x1: 1980, label: "CONVEYOR BELTS",     floor: "rust",    theme: "scrap",   area: 1 },
  { id: "z4_mall",     type: "safe",   x0: 1980, x1: 2420, label: "STRIP MALL",         floor: "asphalt", theme: "scrap",   area: 1 },
  { id: "z5_pit",      type: "combat", x0: 2420, x1: 2960, label: "JUNKYARD PIT",       floor: "rust",    theme: "scrap",   area: 1 },
  { id: "z6_boss",     type: "boss",   x0: 2960, x1: 3200, label: "CRUSHER ARENA",      floor: "rust",    theme: "scrap",   area: 1 },
  // ── AREA 2 · The Automated Assembly Line (Feb 9, 2026) ────────
  // Zone-crossing at x=3200 fires an "AREA 2" fanfare. Music track
  // (clockwork_ballet.mp4) swaps in when the player enters z7+.
  { id: "z7_dock",     type: "combat", x0: 3200, x1: 3980, label: "LOADING DOCK",       floor: "steel",   theme: "factory", area: 2 },
  { id: "z8_break",    type: "safe",   x0: 3980, x1: 4360, label: "BREAK ROOM",         floor: "checker", theme: "factory", area: 2 },
  { id: "z9_lineA",    type: "combat", x0: 4360, x1: 5160, label: "CONVEYOR LINE A",    floor: "steel",   theme: "factory", area: 2 },
  { id: "z10_office",  type: "safe",   x0: 5160, x1: 5540, label: "FOREMAN'S OFFICE",   floor: "concrete",theme: "factory", area: 2 },
  { id: "z11_weld",    type: "combat", x0: 5540, x1: 6180, label: "WELD BAY",           floor: "steel",   theme: "factory", area: 2 },
  { id: "z12_boss2",   type: "boss",   x0: 6180, x1: STAGE_WIDTH, label: "THE ASSEMBLER", floor: "steel", theme: "factory", area: 2 },
];

const ZONE_FLOOR_COLORS = {
  rust:     { a: "#92400E", b: "#B45309" },   // scrap yard oil-stain
  gravel:   { a: "#57534E", b: "#78716C" },   // scrap-yard safe cache
  asphalt:  { a: "#3F3F46", b: "#52525B" },   // strip mall parking lot
  steel:    { a: "#334155", b: "#475569" },   // assembly line steel decking
  checker:  { a: "#0F172A", b: "#F1F5F9" },   // break room checkerboard
  concrete: { a: "#4B5563", b: "#6B7280" },   // foreman's office concrete
};

/** Return the zone whose bounds contain `x`. */
function zoneAt(x) {
  for (const z of ZONES) if (x >= z.x0 && x < z.x1) return z;
  return ZONES[ZONES.length - 1];
}

const WAVES = [
  // ── AREA 1 · Feb 9 pivot — zone-anchored spawns ───────────────
  { subId: "1-1", label: "The Perimeter Gates",   zone_id: "z1_gates", grunts: 6, brute: 0, tint: P.gruntA },
  { subId: "1-2", label: "Supply Cache",           zone_id: "z2_cache", grunts: 0, safe: true },
  { subId: "1-3", label: "The Conveyor Belts",     zone_id: "z3_belts", grunts: 6, brute: 1, tint: P.gruntB },
  { subId: "1-M", label: "Scrap Yard Strip Mall",  zone_id: "z4_mall",  strip_mall: true, style: "outdoor" },
  { subId: "1-4", label: "Junkyard Pit",           zone_id: "z5_pit",   grunts: 5, brute: 1, tint: P.gruntA },
  { subId: "1-5", label: "Junkyard Crusher 9000",  zone_id: "z6_boss",  grunts: 0, boss: true },
  // ── AREA 2 · The Automated Assembly Line (Feb 9, 2026) ────────
  // Grunts here are Line Worker Droids, brutes are Foreman Bots.
  // Weld Bay adds a Conveyor Spider that drops from the ceiling.
  { subId: "2-1", label: "The Loading Dock",       zone_id: "z7_dock",    grunts: 6, brute: 0, tint: P.droidA },
  { subId: "2-2", label: "Break Room",              zone_id: "z8_break",   grunts: 0, safe: true },
  { subId: "2-3", label: "Conveyor Line A",         zone_id: "z9_lineA",   grunts: 6, brute: 1, tint: P.droidB },
  { subId: "2-M", label: "The Conveyor Line Galleria", zone_id: "z10_office", strip_mall: true, style: "indoor", galleria: true },
  { subId: "2-4", label: "The Weld Bay",            zone_id: "z11_weld",   grunts: 4, brute: 1, spider: 2, tint: P.droidA },
  { subId: "2-5", label: "THE ASSEMBLER",           zone_id: "z12_boss2",  grunts: 0, boss: true, boss_kind: "assembler" },
];

// 🏪 Strip Mall shop catalog — extensible. Feb 2026 owner drop:
// added Tea & Sweet Cafe, Scraps Bakery, Book/Gym storefronts, plus a
// hidden Scrap Yard Exclusive kiosk. Every shop carries at least one
// FREE joke/lore item alongside the paid stat items.
const STRIP_MALL_SHOPS = [
  {
    id:        "coin_shop",
    name:      "🪙 COIN SHOP",
    color:     "#F0ABFC",
    awning:    "#7C3AED",
    door:      "#DC2626",
    shopkeeper_tint: "#FCD34D",
    shopkeeper_label: "SHOPKEEPER",
    inventory: [
      {
        id:    "phoenix_emblem",
        name:  "Phoenix Emblem",
        icon:  "🪙",
        price: 2000,
        currency: "chits",
        blurb: "10s total invulnerability. Contact-KOs grunts.",
        limit: 3,
      },
    ],
  },
  {
    id:        "tea_cafe",
    name:      "🍵 TEA & SWEET CAFE",
    color:     "#F9A8D4",
    awning:    "#EC4899",
    door:      "#831843",
    shopkeeper_tint: "#FBCFE8",
    shopkeeper_label: "BARISTA",
    inventory: [
      {
        id:    "placebo_brew",
        name:  "Placebo Brew",
        icon:  "☕",
        price: 0, currency: "chits",
        blurb: "The barista winks and refuses to describe it.",
      },
      {
        id:    "extract",
        name:  "Extract",
        icon:  "💊",
        price: 100, currency: "chits",
        blurb: "Restores 20% HP.",
      },
      {
        id:    "magnet_charm",
        name:  "Magnet Charm",
        icon:  "🧲",
        price: 100, currency: "chits",
        blurb: "Auto-pickup radius (1 block). Upgradeable.",
        limit: 1,
      },
      {
        id:    "salvage_kit",
        name:  "Scrap Yard Salvage Kit",
        icon:  "🛠️",
        price: 100, currency: "chits",
        blurb: "Next broken crate / mini-boss guarantees a rare drop.",
        limit: 1,
      },
    ],
  },
  {
    id:        "scraps_bakery",
    name:      "🥖 SCRAPS BAKERY",
    color:     "#FCD34D",
    awning:    "#B45309",
    door:      "#78350F",
    shopkeeper_tint: "#FEF3C7",
    shopkeeper_label: "BAKER",
    inventory: [
      {
        id:    "fortune_cookie",
        name:  "Fortune Cookie",
        icon:  "🥠",
        price: 0, currency: "chits",
        blurb: "Every crack folds a fortune only the finder can read.",
      },
      {
        id:    "bread_crust",
        name:  "Bread Crust",
        icon:  "🥖",
        price: 300, currency: "chits",
        blurb: "Permanently +1 DEF (damage taken −5%).",
        limit: 1,
      },
      {
        id:    "speed_scone",
        name:  "Speed Scone",
        icon:  "🍰",
        price: 200, currency: "chits",
        blurb: "Permanently +10% movement speed.",
        limit: 1,
      },
      {
        id:    "power_doughnut",
        name:  "Power Doughnut",
        icon:  "🍩",
        price: 900, currency: "chits",
        blurb: "Permanently +5 base ATK.",
        limit: 1,
      },
    ],
  },
  {
    id:        "book_gym",
    name:      "📚 BOOK / GYM",
    color:     "#93C5FD",
    awning:    "#1E40AF",
    door:      "#1E3A8A",
    shopkeeper_tint: "#DBEAFE",
    shopkeeper_label: "TRAINER",
    inventory: [
      {
        id:    "unread_tos",
        name:  "Unread Terms of Service",
        icon:  "📜",
        price: 0, currency: "chits",
        blurb: "A weighty parchment, seal unbroken. The trainer nods approvingly.",
        limit: 1,
      },
      {
        id:    "secret_scroll",
        name:  "Secret Scroll Monthly",
        icon:  "📖",
        price: 1440, currency: "chits",
        blurb: "Permanently +1 combo extension.",
        limit: 1,
      },
      {
        id:    "showtime_illustrated",
        name:  "Showtime Illustrated",
        icon:  "⭐",
        price: 5000, currency: "chits",
        blurb: "+30% coin bonus on Showtime-finisher KOs.",
        limit: 1,
      },
    ],
  },
  {
    id:        "mall_exclusive",
    name:      "🎮 SCRAP YARD EXCLUSIVE",
    color:     "#A78BFA",
    awning:    "#4C1D95",
    door:      "#2E1065",
    shopkeeper_tint: "#DDD6FE",
    shopkeeper_label: "???",
    inventory: [
      {
        id:    "corrupted_save",
        name:  "Corrupted Save File",
        icon:  "💾",
        price: 0, currency: "chits",
        blurb: "The shopkeeper swears they never put this on the shelf.",
        limit: 1,
      },
    ],
  },
];

// ⚙️ Galleria — Area 2 Mall (Feb 9, 2026 owner canon)
// Full inventory sourced from GALLERIA_INVENTORY_CANON_FEB2026.md.
// 4 storefronts: Assembly Line Sweets, Scraps Bakery, Book/Gym +
// Magazine Stand, Galleria Exclusive.
const GALLERIA_SHOPS = [
  {
    id:        "gal_sweets",
    name:      "🍵 ASSEMBLY LINE SWEETS",
    color:     "#FBBF24",
    awning:    "#B45309",
    door:      "#78350F",
    shopkeeper_tint: "#FEF3C7",
    shopkeeper_label: "PASTRY CHEF",
    inventory: [
      {
        id:    "gear_donut",
        name:  "Gear-Molded Donut",
        icon:  "⚙️",
        price: 350, currency: "chits",
        blurb: "Frosted cog stamped hot off the line. Quick sustained fuel.",
      },
      {
        id:    "conveyor_bar",
        name:  "Conveyor Belt Energy Bar",
        icon:  "⚡",
        price: 500, currency: "chits",
        blurb: "Rolls hot off the automated packaging line.",
        limit: 3,
      },
      {
        id:    "placebo_brew",
        name:  "Placebo Brew",
        icon:  "☕",
        price: 0, currency: "chits",
        blurb: "'Grade A Fuel' scrawled on the mug in marker.",
        limit: 1,
      },
    ],
  },
  {
    id:        "gal_bakery",
    name:      "🥖 SCRAPS BAKERY",
    color:     "#FCD34D",
    awning:    "#B45309",
    door:      "#78350F",
    shopkeeper_tint: "#FEF3C7",
    shopkeeper_label: "BAKER",
    inventory: [
      {
        id:    "mushuo_fries",
        name:  "Mushuo-Dusted HomeFries",
        icon:  "🍟",
        price: 250, currency: "chits",
        blurb: "'Eat at your own risk.'",
        limit: 1,
      },
      {
        id:    "gear_danish",
        name:  "Gear-Shaped Danishes",
        icon:  "🥐",
        price: 0, currency: "chits",
        blurb: "Fresh from the warm assembly line tray.",
      },
      {
        id:    "protein_brownie",
        name:  "Protein-Molded Brownie",
        icon:  "🍫",
        price: 1000, currency: "chits",
        blurb: "Stamped like an iron plate. Heavy-duty flex.",
        limit: 1,
      },
      {
        id:    "mikos_blueprints",
        name:  "Miko's Blueprints",
        icon:  "📜",
        price: 5000, currency: "chits",
        blurb: "Held up to the light, faint lines reveal themselves.",
        limit: 1,
      },
      {
        id:    "fortune_cookie",
        name:  "Fortune Cookie",
        icon:  "🥠",
        price: 0, currency: "chits",
        blurb: "'Thank goodness they taste good...'",
        limit: 1,
      },
    ],
  },
  {
    id:        "gal_book_gym",
    name:      "📚 BOOK / GYM & MAGAZINE STAND",
    color:     "#93C5FD",
    awning:    "#1E40AF",
    door:      "#1E3A8A",
    shopkeeper_tint: "#DBEAFE",
    shopkeeper_label: "TRAINER",
    inventory: [
      {
        id:    "vol_v_recoil",
        name:  "Vol V: Ledger's Re-Coil",
        icon:  "📖",
        price: 2500, currency: "chits",
        blurb: "Ledger's most guarded technique. Mass team revive.",
        limit: 1,
      },
      {
        id:    "ledgers_ledger",
        name:  "Ledger's Ledger — Trick Book",
        icon:  "📕",
        price: 2500, currency: "chits",
        blurb: "Cassandra & Ledger's private annotations.",
        limit: 1,
      },
      {
        id:    "irritating_digest",
        name:  "Irritating Digest",
        icon:  "📰",
        price: 100000, currency: "chits",
        blurb: "Tabloid rants about Dragonious Fighters difficulty.",
      },
      {
        id:    "zen_technique",
        name:  "Zen Technique",
        icon:  "🧘",
        price: 1800, currency: "chits",
        blurb: "Tucked into the open magazine rack.",
        limit: 1,
      },
      {
        id:    "piston_manual",
        name:  "Piston Precision Manual",
        icon:  "🔧",
        price: 1200, currency: "chits",
        blurb: "High-pressure pneumatic actuator timing.",
        limit: 1,
      },
      {
        id:    "unread_tos",
        name:  "Unread Terms of Service",
        icon:  "📜",
        price: 0, currency: "chits",
        blurb: "An absurdly thick roll of tractor-feed fine print.",
        limit: 1,
      },
    ],
  },
  {
    id:        "gal_exclusive",
    name:      "💾 GALLERIA EXCLUSIVE",
    color:     "#A78BFA",
    awning:    "#4C1D95",
    door:      "#2E1065",
    shopkeeper_tint: "#DDD6FE",
    shopkeeper_label: "???",
    inventory: [
      {
        id:    "corrupted_save",
        name:  "Corrupted Save File",
        icon:  "💾",
        price: 0, currency: "chits",
        blurb: "A jittering floppy disk resting on the belt.",
        limit: 1,
      },
    ],
  },
];

// 🏪 Strip Mall Shop Overlay — modal that lets the player buy from a shop.
// Handles chit purchases, FREE joke items, and per-run "SOLD OUT" state
// via the `purchases` prop (map of item_id → count already bought).
function StripMallShopOverlay({ shop, chits, emblems, purchases, onClose, onBuy }) {
  const bal = { chits, emblems };
  return (
    <div
      className="absolute inset-0 bg-black/85 flex flex-col p-3 sm:p-4"
      data-testid={`strip-mall-shop-${shop.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div
          className="font-display text-lg sm:text-xl tracking-widest"
          style={{ color: shop.color }}
        >
          {shop.name}
        </div>
        <button
          onClick={onClose}
          className="text-[10px] font-mono uppercase tracking-widest border border-neutral-500 text-neutral-300 hover:border-white hover:text-white px-2 py-1"
          data-testid="strip-mall-shop-close"
        >
          ✕ Leave Shop
        </button>
      </div>
      <div className="text-[10px] font-mono text-neutral-400 uppercase tracking-widest mb-2">
        Balance: <span className="text-[#FBBF24]">🪙 {chits}</span>
        <span className="mx-2 text-neutral-700">·</span>
        <span className="text-[#F0ABFC]">🪙 emblems × {emblems}</span>
      </div>
      <div className="flex-1 overflow-y-auto space-y-2">
        {shop.inventory.map((item) => {
          const isFree    = (item.price || 0) === 0;
          const bought    = purchases?.[item.id] || 0;
          const soldOut   = item.limit != null && bought >= item.limit;
          const canAfford = isFree || bal[item.currency] >= item.price;
          const disabled  = soldOut || !canAfford;
          return (
            <div
              key={item.id}
              className="border border-neutral-700 bg-neutral-900/70 p-2 sm:p-3 flex items-center gap-3"
              data-testid={`shop-item-${item.id}`}
            >
              <div className="text-2xl sm:text-3xl w-10 text-center">{item.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm text-white truncate">{item.name}</div>
                <div className="text-[10px] font-mono text-neutral-400 leading-snug">{item.blurb}</div>
              </div>
              <div className="text-right">
                <div className={`font-mono text-sm ${isFree ? "text-emerald-400" : "text-[#FBBF24]"}`}>
                  {isFree ? "FREE" : `🪙 ${item.price}`}
                </div>
                <button
                  onClick={() => onBuy(item)}
                  disabled={disabled}
                  data-testid={`shop-buy-${item.id}`}
                  className="mt-1 font-display text-[10px] tracking-widest px-3 py-1 border-2 transition-transform active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    borderColor: disabled ? "#4B5563" : shop.color,
                    color: disabled ? "#9CA3AF" : shop.color,
                    background: disabled ? "transparent" : shop.color + "22",
                  }}
                >
                  {soldOut ? "SOLD OUT" : (isFree ? "TAKE" : "BUY")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-center text-[9px] font-mono uppercase tracking-widest text-neutral-500">
        Walk past the shop to continue to the boss.
      </div>
    </div>
  );
}

// ── Utility ─────────────────────────────────────────────────────────
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const rand  = (a, b) => a + Math.random() * (b - a);

// 🪙 Phoenix Emblem HUD coin — CSS-spinning SVG. Red/Black/Purple flashy cycle.
// Rotates on Y-axis (flip effect) using scaleX oscillation. Doubles glow when active.
function PhoenixEmblemCoin({ active }) {
  return (
    <div
      data-testid="phoenix-emblem-coin"
      className={`relative w-9 h-9 flex-shrink-0 ${active ? "phoenix-emblem-coin-active" : ""}`}
      style={{ perspective: "80px" }}
    >
      <div
        className="phoenix-emblem-coin-spin absolute inset-0"
        style={{ transformStyle: "preserve-3d" }}
      >
        <svg viewBox="0 0 40 40" width="36" height="36" aria-hidden="true">
          <defs>
            <radialGradient id="peGradFace" cx="0.5" cy="0.45" r="0.55">
              <stop offset="0%" stopColor="#F0ABFC" />
              <stop offset="45%" stopColor="#7C3AED" />
              <stop offset="100%" stopColor="#0B0F19" />
            </radialGradient>
            <radialGradient id="peGradFlame" cx="0.5" cy="0.65" r="0.5">
              <stop offset="0%" stopColor="#FDE68A" />
              <stop offset="60%" stopColor="#F97316" />
              <stop offset="100%" stopColor="#DC2626" />
            </radialGradient>
          </defs>
          {/* outer rim */}
          <circle cx="20" cy="20" r="19" fill="url(#peGradFace)" stroke="#F0ABFC" strokeWidth="1.5" />
          {/* inner ring */}
          <circle cx="20" cy="20" r="14" fill="none" stroke="#DC2626" strokeWidth="0.8" opacity="0.85" />
          {/* flame core */}
          <ellipse cx="20" cy="22" rx="7" ry="8" fill="url(#peGradFlame)" />
          {/* phoenix silhouette — simple V wings + head */}
          <path
            d="M20 10 L14 17 L11 15 L15 20 L12 24 L20 21 L28 24 L25 20 L29 15 L26 17 Z"
            fill="#0B0F19"
            opacity="0.85"
          />
          {/* red gem accent */}
          <circle cx="20" cy="8" r="1.6" fill="#DC2626" stroke="#FCD34D" strokeWidth="0.4" />
        </svg>
      </div>
    </div>
  );
}

export default function MikoArcadeCanvas({ session, onExit }) {
  const canvasRef = useRef(null);
  const stateRef  = useRef(null);   // mutable game state
  const [ready, setReady]     = useState(false);
  const [hud, setHud]         = useState({
    score: 0, lives: 3, wave: 1, waveLabel: "The Perimeter Gates",
    superBars: 0, bossHp: 0, bossMax: 0, showBoss: false,
    ultBoostActive: false,
    playerHp: session?.hero?.base_hp || 1000,
    playerMax: session?.hero?.base_hp || 1000,
    combo: 0,
    chits: 0,
    emblems: 0,       // 🪙 Phoenix Emblem bag count (transient this run)
    emblemActive: 0,  // seconds remaining on active invulnerability (0 = off)
  });
  const [banner, setBanner]   = useState({ text: "STAGE 1 · THE SCRAP YARDS", sub: "GET READY!", ttl: 120 });
  const [result, setResult]   = useState(null); // "victory" | "gameover"
  const [shopOpen, setShopOpen] = useState(null); // active shop object, null when closed
  const touchRef              = useRef({ left: false, right: false, up: false, down: false, punch: false, kick: false, special: false, emblem: false, interact: false, meta: false });
  const audioRef              = useRef(null);
  const [muted, setMuted]     = useState(false);   // BGM starts unmuted; browsers may block autoplay until first interaction
  const [audioBlocked, setAudioBlocked] = useState(false);
  // 🎶 Which BGM key from ARCADE_MUSIC is currently loaded. Swapped by
  // the game loop as the player crosses into a new Area (Feb 9, 2026).
  const [bgmKey, setBgmKey]   = useState("pocket_world_battle");

  // ── Miko Arcade BGM — swappable per area (Feb 9, 2026) ────────
  // Track changes when `bgmKey` changes (e.g., on Area 2 entry we
  // switch to Clockwork Ballet). Autoplay may be blocked until the
  // user's first click — we surface an UNMUTE prompt in that case.
  useEffect(() => {
    const track = ARCADE_MUSIC[bgmKey];
    if (!track) return;
    // Tear down previous track if any
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.src = ""; } catch (e) { /* noop */ }
      audioRef.current = null;
    }
    const el = new Audio(track.url);
    el.loop = !!track.loop;
    el.volume = muted ? 0 : (track.volume ?? 0.5);
    audioRef.current = el;
    el.play().catch(() => {
      setAudioBlocked(true);
    });
    return () => {
      try { el.pause(); el.src = ""; } catch (e) { /* noop */ }
      audioRef.current = null;
    };
  }, [bgmKey]);   // muted handled by the live-update effect below

  // Live volume/mute updates without re-mounting the audio element.
  // Also handles the boss-swell (louder inside the boss arena zones).
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const track = ARCADE_MUSIC[bgmKey];
    el.volume = muted ? 0 : (track?.volume ?? 0.5);
  }, [muted, bgmKey]);

  const toggleMute = () => {
    const el = audioRef.current;
    setMuted((m) => !m);
    // If the browser blocked autoplay earlier, this click IS the user
    // gesture — retry play now.
    if (el && audioBlocked) {
      el.play().then(() => setAudioBlocked(false)).catch(() => { /* still blocked, keep prompt */ });
    }
  };

  // ── Init game state ───────────────────────────────────────────────
  useEffect(() => {
    if (!session) return;
    const hero = session.hero || {};
    const specials = session.specials || [];
    const heroId = hero.class_id || session.hero_class || "deanah_muki";
    stateRef.current = {
      t: 0,
      camera: 0,
      keys: {},
      touch: touchRef.current,
      heroId,
      specialsByBar: {
        1: specials.find((s) => (s.slot || s.meter_bar) === 1) || null,
        2: specials.find((s) => (s.slot || s.meter_bar) === 2) || null,
        3: specials.find((s) => (s.slot || s.meter_bar) === 3) || null,
      },
      player: {
        x: 80, y: GROUND_Y, vx: 0, vy: 0,
        hp: hero.base_hp || 1000, maxHp: hero.base_hp || 1000,
        patk: hero.base_patk || 24, matk: hero.base_matk || 34,
        speed: (hero.move_speed || 4) * 0.9,
        facing: 1, state: "idle", stateT: 0, iframes: 0,
        onGround: true,
        superMeter: 0, superMax: 300,   // 3 bars × 100 units
        // ⚡ Conveyor Belt Energy Bar timer (Feb 2026, Galleria drop) —
        // while > 0, superMeter is force-locked at max so the player has
        // all three ★ Ults ready for the duration.
        ultBoostT: 0,
        specials,
        combo: 0, comboTimer: 0,
        // 🏪 Strip Mall permanent buff stacks (Bread Crust / Speed Scone
        // / Power Doughnut). `defBonus` reduces incoming damage by 5%
        // per rank; `atkBonus` is a flat addend to `patk` on lookup;
        // `speedBonus` multiplies base move speed.
        atkBonus: 0, defBonus: 0, speedBonus: 0,
        // 🥊 Simple combos (Feb 2026) — P,P,P and P,P,K strings.
        // Buffer holds the last 3 attack inputs; each press bumps
        // `hitStreak` iff the swing actually connected with an enemy
        // or boss. When buffer matches a canonical string AND hitStreak
        // >= 3, the combo Finisher fires (see triggerFinisher below).
        inputBuffer: [], bufferT: 0, hitStreak: 0,
        // ✨ Signature META (Feb 2026, per-hero unique layer).
        //   metaCd  — cooldown countdown
        //   metaT   — active-window countdown (for timed abilities)
        //   flyT    — Miko Hoot-Dash float duration
        //   upTapT  — Miko double-tap ↑ detector
        //   hitCount— David Sizzling Sear 3rd-hit trigger
        //   fireT   — David fire aura countdown
        //   anchor  — Cassandra Ledger Anchor { x, placed }
        //   orbA    — Sollor Orbital Watch orbit angle (radians)
        //   orbCd   — Sollor orbital shove cooldown
        //   pounceCd— Muki Pounce cooldown
        metaCd: 0, metaT: 0,
        flyT: 0, upTapT: 0, _upWas: false,
        hitCount: 0, fireT: 0,
        anchor: null,
        orbA: 0, orbCd: 0,
        pounceCd: 0,
        specialActive: null, specialT: 0,
      },
      enemies: [],
      bubbles: [],
      particles: [],
      pickups: [],           // dollar drops (BARF!)
      breakables: [],        // 🪵 crates + drums scattered on the stage
      drops: [],             // 🪙 items that flew out of a broken crate
      wave: 0,
      waveState: "intro",    // intro → active → cleared → next
      waveTimer: 90,
      boss: null,
      levelComplete: false,
      lives: 3,
      score: 0,
      hitsLanded: 0,
      chits: 0,              // 🪙 chit coin balance for this arcade run
      emblems: 0,            // 🪙 Phoenix Emblem count (transient — drops evaporate at run end)
      emblemT: 0,            // countdown ticks while emblem invulnerability is active
      startedAt: Date.now(),
      startedAtSec: performance.now() / 1000,
      shake: 0,
      // 🥊 Combo Finisher FX — { type: "PPP"|"PPK", t: countdown_frames }
      finisherFX: null,
      // 🏪 Strip Mall purchases (per-run) — { item_id: count }. Used to
      // enforce shop `limit` and to persist HUD badges across scenes.
      purchases: {},
      badges: [],           // ["system_anomaly", "i_agree", ...]
      // Placebo Brew active timer (frames). Purely cosmetic sparkle.
      placeboT: 0,
      // 💾 Corrupted Save File one-shot glitch FX
      glitchFX: null,       // { t: frames_left }
      // 🎯 Flags for shop items whose full mechanics land in future drops
      flags: {
        magnet_charm:         false,   // pickup-radius upgrade
        salvage_kit_pending:  false,   // next crate → rare drop
        combo_extension:      0,       // stacks per Secret Scroll
        showtime_coin_bonus:  false,   // +30% coin on Showtime KO
        // ── Galleria Mall (Area 2) additions ─────────────────────
        kbMult:               1.0,     // Piston Precision Manual ×1.2
        proteinAura:          false,   // Protein-Molded Brownie visual
        zenPassive:           false,   // Zen Technique — passive ult fill
        mushuoPassive:        false,   // Mushuo HomeFries — 50/50 half-HP proc
        mushuoArmed:          true,    // resets when HP climbs back over 60%
        reviveOnKO:           false,   // Vol V — placeholder for multi-hero
        ledgersTricks:        false,   // Ledger's Ledger — placeholder
        moveTracker:          false,   // Irritating Digest — placeholder
        blueprintsDelivered:  false,   // Miko Blueprints — key upgrade item
      },
    };
    // 🪵 Seed the stage with breakable containers — per-zone catalog
    // (Feb 9, 2026 pivot). Every combat zone gets ~5 breakables + 1
    // multi-hit wall; safe zones get a dense stash of crates + drums;
    // boss zone stays clean. Oil Drums (2 HP) and Tire Stacks (3 HP)
    // are new multi-hit types with richer loot on smash.
    stateRef.current.breakables = [
      // ── Zone 1 (Scrap Gates) — combat, 0..820 ──────────────
      { x:  180, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",     broken: false, zone_id: "z1_gates" },
      { x:  340, y: GROUND_Y, w: 32, h: 40, hp: 2, maxHp: 2, type: "oil_drum",  broken: false, zone_id: "z1_gates" },
      { x:  500, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",     broken: false, zone_id: "z1_gates" },
      { x:  660, y: GROUND_Y, w: 40, h: 30, hp: 3, maxHp: 3, type: "tire_stack",broken: false, zone_id: "z1_gates" },

      // ── Zone 2 (Supply Cache) — SAFE, 820..1180 ────────────
      // Dense reward stash — the "why bother going back" pay-off.
      { x:  880, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",     broken: false, zone_id: "z2_cache" },
      { x:  940, y: GROUND_Y, w: 32, h: 40, hp: 2, maxHp: 2, type: "oil_drum",  broken: false, zone_id: "z2_cache" },
      { x: 1000, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",     broken: false, zone_id: "z2_cache" },
      { x: 1060, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",     broken: false, zone_id: "z2_cache" },
      { x: 1120, y: GROUND_Y, w: 40, h: 30, hp: 3, maxHp: 3, type: "tire_stack",broken: false, zone_id: "z2_cache" },

      // ── Zone 3 (Conveyor Belts) — combat, 1180..1980 ───────
      { x: 1240, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",     broken: false, zone_id: "z3_belts" },
      { x: 1360, y: GROUND_Y, w: 32, h: 40, hp: 2, maxHp: 2, type: "oil_drum",  broken: false, zone_id: "z3_belts" },
      { x: 1480, y: GROUND_Y, w: 42, h: 78, hp: 4, maxHp: 4, type: "wall",      broken: false, zone_id: "z3_belts" },
      { x: 1620, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",     broken: false, zone_id: "z3_belts" },
      { x: 1740, y: GROUND_Y, w: 32, h: 40, hp: 2, maxHp: 2, type: "oil_drum",  broken: false, zone_id: "z3_belts" },
      { x: 1860, y: GROUND_Y, w: 40, h: 30, hp: 3, maxHp: 3, type: "tire_stack",broken: false, zone_id: "z3_belts" },

      // ── Zone 4 (Strip Mall) — safe, 1980..2420 ─────────────
      // Mall keeps a couple stashed drums between the storefronts.
      { x: 2050, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",     broken: false, zone_id: "z4_mall"  },
      { x: 2380, y: GROUND_Y, w: 32, h: 40, hp: 2, maxHp: 2, type: "oil_drum",  broken: false, zone_id: "z4_mall"  },

      // ── Zone 5 (Junkyard Pit) — combat, 2420..2960 ─────────
      { x: 2480, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",     broken: false, zone_id: "z5_pit"   },
      { x: 2600, y: GROUND_Y, w: 32, h: 40, hp: 2, maxHp: 2, type: "oil_drum",  broken: false, zone_id: "z5_pit"   },
      { x: 2720, y: GROUND_Y, w: 42, h: 78, hp: 4, maxHp: 4, type: "wall",      broken: false, zone_id: "z5_pit"   },
      { x: 2840, y: GROUND_Y, w: 40, h: 30, hp: 3, maxHp: 3, type: "tire_stack",broken: false, zone_id: "z5_pit"   },
      { x: 2900, y: GROUND_Y, w: 32, h: 40, hp: 2, maxHp: 2, type: "oil_drum",  broken: false, zone_id: "z5_pit"   },

      // ── AREA 2 · The Automated Assembly Line (Feb 9, 2026) ─────
      // Zone 7 (Loading Dock) — combat, 3200..3980
      { x: 3300, y: GROUND_Y, w: 34, h: 40, hp: 2, maxHp: 2, type: "metal_barrel",   broken: false, zone_id: "z7_dock"   },
      { x: 3460, y: GROUND_Y, w: 28, h: 32, hp: 1, maxHp: 1, type: "circuit_panel", broken: false, zone_id: "z7_dock"   },
      { x: 3620, y: GROUND_Y, w: 34, h: 40, hp: 2, maxHp: 2, type: "metal_barrel",   broken: false, zone_id: "z7_dock"   },
      { x: 3800, y: GROUND_Y, w: 28, h: 32, hp: 1, maxHp: 1, type: "circuit_panel", broken: false, zone_id: "z7_dock"   },

      // Zone 8 (Break Room) — SAFE, 3980..4360 — dense rest-stop rewards
      { x: 4020, y: GROUND_Y, w: 36, h: 64, hp: 3, maxHp: 3, type: "vending_machine", broken: false, zone_id: "z8_break" },
      { x: 4100, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",           broken: false, zone_id: "z8_break" },
      { x: 4180, y: GROUND_Y, w: 36, h: 64, hp: 3, maxHp: 3, type: "vending_machine", broken: false, zone_id: "z8_break" },
      { x: 4260, y: GROUND_Y, w: 34, h: 40, hp: 2, maxHp: 2, type: "metal_barrel",    broken: false, zone_id: "z8_break" },

      // Zone 9 (Conveyor Line A) — combat, 4360..5160
      { x: 4430, y: GROUND_Y, w: 34, h: 40, hp: 2, maxHp: 2, type: "metal_barrel",   broken: false, zone_id: "z9_lineA"  },
      { x: 4580, y: GROUND_Y, w: 28, h: 32, hp: 1, maxHp: 1, type: "circuit_panel", broken: false, zone_id: "z9_lineA"  },
      { x: 4740, y: GROUND_Y, w: 42, h: 78, hp: 4, maxHp: 4, type: "wall",          broken: false, zone_id: "z9_lineA"  },
      { x: 4880, y: GROUND_Y, w: 34, h: 40, hp: 2, maxHp: 2, type: "metal_barrel",   broken: false, zone_id: "z9_lineA"  },
      { x: 5040, y: GROUND_Y, w: 28, h: 32, hp: 1, maxHp: 1, type: "circuit_panel", broken: false, zone_id: "z9_lineA"  },

      // Zone 10 (Foreman's Office) — safe, 5160..5540
      { x: 5220, y: GROUND_Y, w: 36, h: 64, hp: 3, maxHp: 3, type: "vending_machine", broken: false, zone_id: "z10_office" },
      { x: 5400, y: GROUND_Y, w: 32, h: 32, hp: 1, maxHp: 1, type: "crate",           broken: false, zone_id: "z10_office" },

      // Zone 11 (Weld Bay) — combat, 5540..6180
      { x: 5620, y: GROUND_Y, w: 34, h: 40, hp: 2, maxHp: 2, type: "metal_barrel",   broken: false, zone_id: "z11_weld"  },
      { x: 5760, y: GROUND_Y, w: 28, h: 32, hp: 1, maxHp: 1, type: "circuit_panel", broken: false, zone_id: "z11_weld"  },
      { x: 5900, y: GROUND_Y, w: 42, h: 78, hp: 4, maxHp: 4, type: "wall",          broken: false, zone_id: "z11_weld"  },
      { x: 6060, y: GROUND_Y, w: 34, h: 40, hp: 2, maxHp: 2, type: "metal_barrel",   broken: false, zone_id: "z11_weld"  },
    ];
    setReady(true);
  }, [session]);

  // ── Keyboard input ────────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return;
    const down = (e) => {
      const s = stateRef.current; if (!s) return;
      const k = e.key.toLowerCase();
      s.keys[k] = true;
      // prevent scroll on space/arrows
      if (["arrowup","arrowdown","arrowleft","arrowright"," ","j","k","l","i","e","u"].includes(k)) {
        e.preventDefault();
      }
    };
    const up = (e) => {
      const s = stateRef.current; if (!s) return;
      s.keys[e.key.toLowerCase()] = false;
    };
    window.addEventListener("keydown", down, { passive: false });
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [ready]);

  // ── Main loop ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return;
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    let raf;

    const spawnWave = (wIdx) => {
      const s = stateRef.current;
      const w = WAVES[wIdx];
      if (!w) return;
      // 🗺️ Every wave is anchored to a zone (Feb 9, 2026 pivot).
      const zone = ZONES.find((z) => z.id === w.zone_id) || ZONES[0];
      s.currentZoneId = zone.id;
      // 🏪 Strip Mall interlude — no combat, spawn a shopfront + shopkeeper.
      // Placement: dynamically left-shift shopX so ALL storefronts (5 as of
      // Feb 2026) plus the 260 px "walk past to advance" buffer fit inside
      // STAGE_WIDTH (3200 px), regardless of how many shops we add.
      if (w.strip_mall) {
        // Area 2 (Foreman's Office → The Conveyor Line Galleria) uses
        // the GALLERIA_SHOPS catalog; Area 1's Scrap Yard mall keeps
        // the original STRIP_MALL_SHOPS set.
        const shopSet = w.galleria ? GALLERIA_SHOPS : STRIP_MALL_SHOPS;
        const spacing = 220;
        const shopX = STAGE_WIDTH - 900 - (shopSet.length - 1) * spacing;
        s.stripMall = {
          style:    w.style || "outdoor",
          galleria: !!w.galleria,
          x:        shopX,
          shops:    shopSet.map((shop, i) => ({
            ...shop,
            x:      shopX + i * spacing,
            y:      GROUND_Y,
            w:      160,
            h:      140,
          })),
          visited: false,
        };
        setBanner({
          text: w.galleria ? "⚙️ CONVEYOR LINE GALLERIA" : "🏪 STRIP MALL",
          sub:  (w.label || "").toUpperCase(),
          ttl:  100,
        });
        // Auto-advance to boss wave when player walks past the shop zone
        // (see wave manager below).
        return;
      }
      // 🟢 Safe zone — non-combat rest area. Cleared by walking past
      // the zone's right edge; no enemy spawn.
      if (w.safe) {
        setBanner({ text: "🟢 SAFE ZONE", sub: (w.label || "").toUpperCase(), ttl: 100 });
        return;
      }
      if (w.boss) {
        s.boss = {
          x: zone.x0 + (zone.x1 - zone.x0) * 0.65,
          y: GROUND_Y - 30, vx: 0, vy: 0,
          hp: 900, maxHp: 900,
          state: "idle", stateT: 0, aiT: 60,
          size: 1.8, tint: P.boss,
          zone_id: zone.id,
          kind: w.boss_kind || "crusher",
        };
        setHud((h) => ({ ...h, bossMax: 900, bossHp: 900, showBoss: true }));
      } else {
        // Grunts patrol the whole zone. Space them evenly across the
        // zone's playable interior so combat feels arena-shaped instead
        // of a conga line rolling toward the player.
        const zW = zone.x1 - zone.x0;
        const inset = 60;
        for (let i = 0; i < (w.grunts || 0); i++) {
          const t = (i + 1) / ((w.grunts || 0) + 1);
          s.enemies.push({
            x: zone.x0 + inset + (zW - inset * 2) * t,
            y: GROUND_Y, vx: 0, vy: 0,
            hp: 60, maxHp: 60,
            state: "walk", stateT: 0, aiT: rand(30, 90),
            facing: i % 2 === 0 ? -1 : 1,
            tint: w.tint,
            size: 1,
            kind: "grunt",
            zone_id: zone.id,
            zoneX0:  zone.x0 + 20,
            zoneX1:  zone.x1 - 20,
          });
        }
        // 💪 Brute — bigger, angrier scrap-yard heavy. Same AI, more HP,
        // more punch. Placed roughly in the middle of the zone.
        for (let i = 0; i < (w.brute || 0); i++) {
          const cx = zone.x0 + (zone.x1 - zone.x0) * (0.4 + 0.2 * i);
          s.enemies.push({
            x: cx, y: GROUND_Y, vx: 0, vy: 0,
            hp: 180, maxHp: 180,
            state: "walk", stateT: 0, aiT: rand(40, 120),
            facing: -1,
            tint: zone.theme === "factory" ? P.foreman : "#57534E",
            size: 1.5,
            kind: zone.theme === "factory" ? "foreman" : "brute",
            zone_id: zone.id,
            zoneX0:  zone.x0 + 20,
            zoneX1:  zone.x1 - 20,
            dmg: 12,
          });
        }
        // 🕷️ Conveyor Spider — drops from the ceiling (Area 2 Weld Bay).
        // Starts above the canvas, falls to the ground, then walks slowly.
        // Low HP but tricky vertical entry the player can't see coming.
        for (let i = 0; i < (w.spider || 0); i++) {
          const cx = zone.x0 + (zone.x1 - zone.x0) * (0.3 + 0.35 * i);
          s.enemies.push({
            x: cx, y: -40, vx: 0, vy: 0,
            hp: 40, maxHp: 40,
            state: "drop", stateT: 90,   // hangs from web for 1.5s
            aiT: rand(20, 60),
            facing: -1,
            tint: P.spider,
            size: 1,
            kind: "spider",
            zone_id: zone.id,
            zoneX0:  zone.x0 + 20,
            zoneX1:  zone.x1 - 20,
            dmg: 8,
            webAnchorY: -40,  // for drawing the strand
            dropTargetY: GROUND_Y,
          });
        }
      }
    };

    const addBubble = (text, x, y, color) => {
      stateRef.current.bubbles.push({
        text, x, y: y - 30, vy: -1.5, ttl: 45, life: 45, color,
        rot: rand(-0.3, 0.3),
      });
    };
    const addParticles = (x, y, color, n = 8) => {
      for (let i = 0; i < n; i++) {
        stateRef.current.particles.push({
          x, y, vx: rand(-3, 3), vy: rand(-4, -1),
          color, ttl: 30, life: 30, size: rand(2, 5),
        });
      }
    };

    // 🪵 Break a container — spawn coin drops that arc into the air
    // Wall = larger container, richer payload; Crate = 1–3 chits.
    // 🪙 Phoenix Emblem (Feb 2026 canon): 0.5% chance per crate, 1.5% for walls.
    // On a successful roll we ADD a single emblem drop on top of the chits.
    // 🪵 Break a container — spawn coin drops that arc into the air
    // Wall = larger container, richer payload; Oil Drum + Tire Stack = mid;
    // Crate = 1–3 chits. 🪙 Phoenix Emblem (Feb 2026 canon): 0.5% per
    // crate, 1.5% for walls, 1.0% for drums, 0.75% for tire stacks.
    const spawnDrops = (crate) => {
      const s = stateRef.current;
      const isWall  = crate.type === "wall";
      const isDrum  = crate.type === "oil_drum";
      const isTire  = crate.type === "tire_stack";
      const isMetal = crate.type === "metal_barrel";
      const isVend  = crate.type === "vending_machine";
      const isCircuit = crate.type === "circuit_panel";
      const count = isWall
        ? (9 + Math.floor(Math.random() * 6))    // wall = 9..14 chits
        : isVend
          ? (12 + Math.floor(Math.random() * 7)) // vending = 12..18 chits (jackpot!)
          : (isDrum || isTire || isMetal)
            ? (5 + Math.floor(Math.random() * 4))// drums/tires/barrels = 5..8 chits
            : isCircuit
              ? (3 + Math.floor(Math.random() * 3)) // circuit panel = 3..5 chits
              : (3 + Math.floor(Math.random() * 4));// crate = 3..6 chits
      for (let i = 0; i < count; i++) {
        s.drops.push({
          x: crate.x + rand(-6, 6),
          y: crate.y - crate.h,
          vx: rand(-2.5, 2.5),
          vy: rand(-6, -3),
          ttl: 240 + i * 3,
          resting: false,
          type: "chit",
        });
      }
      // 🪙 Phoenix Emblem roll — factory containers get slightly higher
      // odds (denser assembly line = more loot).
      const emblemChance =
        isWall    ? 0.015 :
        isVend    ? 0.02  :   // richest drop
        isMetal   ? 0.012 :
        isDrum    ? 0.01  :
        isTire    ? 0.0075:
        isCircuit ? 0.008 :
                    0.005;    // plain crate
      if (Math.random() < emblemChance || s.salvage_kit_pending) {
        s.salvage_kit_pending = false;
        s.drops.push({
          x: crate.x + rand(-4, 4),
          y: crate.y - crate.h - 8,
          vx: rand(-1.5, 1.5),
          vy: rand(-7, -5),
          ttl: 360,
          resting: false,
          type: "emblem",
        });
        addBubble("PHOENIX EMBLEM!", crate.x - s.camera, crate.y - crate.h - 30, "#F0ABFC");
      }
    };

    // Attack-box vs breakable overlap check — call from punch/kick/etc.
    // ax/ay = center of the attack hitbox, aw/ah = half-extents, dmg = damage
    const hitBreakables = (ax, ay, aw, ah, dmg) => {
      const s = stateRef.current;
      s.breakables.forEach((c) => {
        if (c.broken) return;
        const cxOverlap = Math.abs(c.x - ax) < aw + c.w / 2;
        const cyOverlap = Math.abs((c.y - c.h / 2) - ay) < ah + c.h / 2;
        if (cxOverlap && cyOverlap) {
          c.hp -= dmg;
          if (c.hp <= 0) {
            c.broken = true;
            spawnDrops(c);
            const isWall = c.type === "wall";
            addParticles(c.x, c.y - c.h / 2, isWall ? "#94A3B8" : "#78350F", isWall ? 24 : 12);
            addParticles(c.x, c.y - c.h / 2, "#FBBF24", isWall ? 14 : 8);
            addBubble(isWall ? "SMASH!" : "KRAK!", c.x - s.camera, c.y - c.h - 10, isWall ? "#F0ABFC" : "#FBBF24");
            s.score += isWall ? 60 : 15;
            s.shake = Math.max(s.shake, isWall ? 8 : 4);
          } else {
            addParticles(c.x, c.y - c.h / 2, c.type === "wall" ? "#94A3B8" : "#78350F", 4);
          }
        }
      });
    };

    const damageEnemy = (e, dmg, kind) => {
      const s = stateRef.current;
      e.hp -= dmg;
      e.state = "hit"; e.stateT = 12;
      // 🔧 Piston Precision Manual — permanent +20% knockback (kbMult).
      const kb = 4 * (s.flags.kbMult || 1);
      e.vx = s.player.facing * kb;
      e.vy = -3;
      s.hitsLanded += 1;
      s.player.combo += 1;
      s.player.comboTimer = 90;
      // 👨‍🍳 David — Sizzling Sear counter (every 3rd landed hit → aura)
      if (s.heroId === "david_lauer" && kind !== "super1" && kind !== "super2" && kind !== "super3") {
        s.player.hitCount = (s.player.hitCount || 0) + 1;
        if (s.player.hitCount >= 3 && s.player.fireT <= 0 && s.player.metaCd <= 0) {
          s.player.hitCount = 0;
          s.player.fireT = 90;
          s.player.metaCd = 240;
          addBubble("SIZZLE!", s.player.x - s.camera, s.player.y - 60, "#F97316");
          addParticles(s.player.x - s.camera, s.player.y - 20, "#F97316", 12);
        }
      }
      s.score += 25 * (1 + Math.floor(s.player.combo / 3));
      s.player.superMeter = clamp(s.player.superMeter + 8, 0, s.player.superMax);
      s.shake = Math.max(s.shake, 4);
      const bubbleMap = {
        punch: { txt: ["POW!","BAM!","THWACK!"][Math.floor(Math.random()*3)], color: P.bubble1 },
        kick:  { txt: ["SMACK!","KRAKK!","WHAMM!"][Math.floor(Math.random()*3)], color: P.bubble2 },
        super1:{ txt: "CYCLONE!", color: P.bubble1 },
        super2:{ txt: "VORTEX!",  color: P.bubble2 },
        super3:{ txt: "METEOR!",  color: P.bubble3 },
      };
      const b = bubbleMap[kind] || bubbleMap.punch;
      addBubble(b.txt, e.x - s.camera, e.y - 40, b.color);
      addParticles(e.x - s.camera, e.y - 20, e.tint, 6);
      if (e.hp <= 0) {
        addBubble("K.O.!", e.x - s.camera, e.y - 60, P.bubble3);
        s.score += 100;
        // BARF! → coin/chit burst (RCR canon). Feb 9, 2026 owner bump —
        // enemies now drop actual chits (not just score pickups) so the
        // wallet fills at a real pace: grunts 3..5, brutes/foreman
        // 6..10, spiders 4..6. Chits are picked up on contact and
        // added to s.chits by the drops loop.
        addBubble("BARF!", e.x - s.camera + 20, e.y - 30, P.bubble4);
        const isBig = e.kind === "brute" || e.kind === "foreman";
        const isSpi = e.kind === "spider";
        const nCoins = isBig ? (6 + Math.floor(Math.random() * 5))
                      : isSpi ? (4 + Math.floor(Math.random() * 3))
                              : (3 + Math.floor(Math.random() * 3));
        for (let i = 0; i < nCoins; i++) {
          s.drops.push({
            x: e.x + rand(-8, 8),
            y: e.y - 10,
            vx: rand(-2.5, 2.5),
            vy: rand(-6, -3),
            ttl: 260 + i * 5,
            resting: false,
            type: "chit",
          });
        }
        // Small chance of a Phoenix Emblem from bigger KOs
        if (isBig && Math.random() < 0.08) {
          s.drops.push({
            x: e.x + rand(-4, 4),
            y: e.y - 20,
            vx: rand(-1.5, 1.5),
            vy: rand(-7, -5),
            ttl: 360,
            resting: false,
            type: "emblem",
          });
          addBubble("PHOENIX EMBLEM!", e.x - s.camera, e.y - 50, "#F0ABFC");
        }
      }
    };

    const damagePlayer = (dmg) => {
      const s = stateRef.current;
      if (s.player.iframes > 0) return;
      // Sollor's Polarity Overload — 50% incoming mitigation while active
      let finalDmg = (s.player.polarityBuffT || 0) > 0 ? dmg * 0.5 : dmg;
      // 🥖 Bread Crust — permanent −5% incoming damage per rank
      if ((s.player.defBonus || 0) > 0) {
        finalDmg *= Math.max(0.5, 1 - 0.05 * s.player.defBonus);
      }
      s.player.hp -= finalDmg;
      s.player.iframes = 45;
      s.player.state = "hit"; s.player.stateT = 15;
      s.player.combo = 0;
      s.shake = Math.max(s.shake, 6);
      addParticles(s.player.x - s.camera, s.player.y - 30, P.deanah, 10);
      // 🍟 Mushuo-Dusted HomeFries — when HP crosses to ≤50% while
      // armed, 50% chance to instantly fill the Super Meter by +50%
      // (≈ 1.5 ★ bars). Re-arms once HP climbs back above 60%.
      if (
        s.flags.mushuoPassive &&
        s.flags.mushuoArmed &&
        s.player.hp > 0 &&
        s.player.hp <= s.player.maxHp * 0.5
      ) {
        s.flags.mushuoArmed = false;
        if (Math.random() < 0.5) {
          s.player.superMeter = clamp(
            s.player.superMeter + s.player.superMax * 0.5,
            0, s.player.superMax,
          );
          setBanner({ text: "🍟 MUSHUO DUST!", sub: "★ +1.5 BARS", ttl: 90 });
          addParticles(s.player.x - s.camera, s.player.y - 40, "#F0ABFC", 14);
        } else {
          setBanner({ text: "🍟 …NOTHING.", sub: "THE DUST WAS FAKE.", ttl: 70 });
        }
      }
      if (s.player.hp <= 0) {
        s.lives -= 1;
        if (s.lives <= 0) {
          setResult("gameover");
        } else {
          s.player.hp = s.player.maxHp;
          s.player.iframes = 120;
          setBanner({ text: "OUCH!", sub: "TRY AGAIN — 1UP", ttl: 90 });
        }
      }
    };

    const step = () => {
      const s = stateRef.current;
      if (!s) return;
      s.t += 1;
      if (s.shake > 0) s.shake *= 0.85;

      // ⚡ / 🧘 Galleria passives (Feb 2026):
      //   • Conveyor Belt Energy Bar → force-lock Super Meter to max
      //     (all 3 ★ bars ready) for 10 seconds.
      //   • Zen Technique → passively drip the Super Meter (~+0.3/frame
      //     ≈ +18/sec, so a full 300-unit gauge fills in ~17s idle).
      //   • Mushuo HomeFries → re-arm the 50% proc when HP recovers.
      if (s.player.ultBoostT > 0) {
        s.player.ultBoostT -= 1;
        s.player.superMeter = s.player.superMax;
      } else if (s.flags.zenPassive && s.player.hp > 0) {
        s.player.superMeter = clamp(
          s.player.superMeter + 0.3, 0, s.player.superMax,
        );
      }
      if (
        s.flags.mushuoPassive &&
        !s.flags.mushuoArmed &&
        s.player.hp > s.player.maxHp * 0.6
      ) {
        s.flags.mushuoArmed = true;
      }

      // ── Wave manager ────────────────────────────────────────────
      if (s.waveState === "intro") {
        s.waveTimer -= 1;
        if (s.waveTimer <= 0) {
          spawnWave(s.wave);
          s.waveState = "active";
        }
      } else if (s.waveState === "active") {
        const currentWave = WAVES[s.wave];
        const currentZone = ZONES.find((z) => z.id === currentWave?.zone_id);
        // 🏪 Strip Mall — cleared when player walks past the last shop's zone.
        if (currentWave?.strip_mall) {
          const mall = s.stripMall;
          if (mall) {
            const lastShop = mall.shops[mall.shops.length - 1];
            // Advance to boss wave when player walks past the final shop
            if (s.player.x > (lastShop?.x || mall.x) + 260) {
              s.stripMall = null;
              s.shopOpen = null;   // safety close if left open
              s.wave += 1;
              s.waveState = "intro";
              s.waveTimer = 90;
              const nextW = WAVES[s.wave];
              if (nextW) {
                setBanner({ text: `AREA 1-${s.wave + 1}`, sub: nextW.label.toUpperCase(), ttl: 100 });
                setHud((h) => ({ ...h, wave: s.wave + 1, waveLabel: nextW.label }));
              }
            }
          }
        } else if (currentWave?.safe) {
          // 🟢 Safe zone — no combat; advance when player walks past the
          // zone's right edge. Player can freely backtrack (Feb 9 pivot).
          if (currentZone && s.player.x > currentZone.x1 - 20 && s.wave < WAVES.length - 1) {
            s.wave += 1;
            s.waveState = "intro";
            s.waveTimer = 60;
            const nextW = WAVES[s.wave];
            if (nextW) {
              setBanner({ text: `AREA 1-${s.wave + 1}`, sub: nextW.label.toUpperCase(), ttl: 100 });
              setHud((h) => ({ ...h, wave: s.wave + 1, waveLabel: nextW.label }));
            }
          }
        } else {
          const noEnemies = s.enemies.length === 0 && !s.boss;
          if (noEnemies && s.wave < WAVES.length - 1) {
            // 🕒 NO-IDLE rule (Feb 2026): required kills are done, but we
            // do NOT force-advance. Enter "cleared" state — the game
            // keeps spawning bonus grunts so the player can grind coins
            // and combos until they walk to the right edge of the stage.
            if ((currentWave?.grunts || 0) > 0) {
              s.waveState = "cleared";
              s.clearedT = 0;
              setBanner({
                text: "AREA CLEARED",
                sub:  "→ WALK RIGHT TO ADVANCE · OR KEEP GRINDING",
                ttl:  120,
              });
            } else {
              // Zero-grunt waves (e.g. cinematic) still auto-advance.
              s.wave += 1;
              s.waveState = "intro";
              s.waveTimer = 90;
              const w = WAVES[s.wave];
              setBanner({ text: `AREA 1-${s.wave + 1}`, sub: w.label.toUpperCase(), ttl: 100 });
              setHud((h) => ({ ...h, wave: s.wave + 1, waveLabel: w.label }));
            }
          } else if (s.boss && s.boss.hp <= 0) {
            s.boss = null;
            s.levelComplete = true;
            setResult("victory");
          }
        }
      } else if (s.waveState === "cleared") {
        // 🕒 Lingering — bonus grunts drip-spawn every ~6 seconds while
        // the player farms. Concurrent cap = 3 so the screen stays
        // readable. Player advances by walking past the CURRENT ZONE's
        // right edge (Feb 9 pivot — was stage edge).
        s.clearedT = (s.clearedT || 0) + 1;
        const currentWave = WAVES[s.wave];
        const currentZone = ZONES.find((z) => z.id === currentWave?.zone_id);
        const tint = currentWave?.tint || P.gruntA;
        // Every 360 frames (~6s) drop a bonus grunt if under the cap.
        // Spawn WITHIN the current zone so grunts don't crash the safe
        // zone or wander off the map.
        if (s.clearedT % 360 === 0 && s.enemies.length < 3 && currentZone) {
          const side = (s.clearedT / 360) % 2 === 0 ? 1 : -1;
          const spawnX = clamp(
            side === 1 ? Math.min(s.player.x + 340, currentZone.x1 - 40)
                       : Math.max(s.player.x - 300, currentZone.x0 + 40),
            currentZone.x0 + 20,
            currentZone.x1 - 20,
          );
          s.enemies.push({
            x: spawnX, y: GROUND_Y, vx: 0, vy: 0,
            hp: 60, maxHp: 60,
            state: "walk", stateT: 0, aiT: rand(30, 90),
            facing: side === 1 ? -1 : 1,
            tint,
            size: 1,
            kind:    "grunt",
            zone_id: currentZone.id,
            zoneX0:  currentZone.x0 + 20,
            zoneX1:  currentZone.x1 - 20,
            bonus: true,
          });
        }
        // Advance trigger: walk past the current zone's right edge.
        const advanceX = currentZone ? currentZone.x1 - 20 : STAGE_WIDTH - 200;
        if (s.player.x > advanceX && s.wave < WAVES.length - 1) {
          s.wave += 1;
          s.waveState = "intro";
          s.waveTimer = 90;
          // Clear lingering bonus grunts so the next wave's roster
          // reads clean (no leftover mobs polluting the next spawn).
          s.enemies = [];
          const w = WAVES[s.wave];
          setBanner({ text: `AREA 1-${s.wave + 1}`, sub: w.label.toUpperCase(), ttl: 100 });
          setHud((h) => ({ ...h, wave: s.wave + 1, waveLabel: w.label }));
        }
      }

      // ── 🗺️ Zone-crossing banner (Feb 9 pivot) ───────────────
      // Fires a subtle "→ ZONE NAME" tag the first time you enter a
      // zone. Also stamps the current zone id so backtracking hints
      // ("← Backtrack") can trigger on re-entry. Feb 9, 2026: also
      // swaps the BGM when the player crosses into a new AREA and
      // pumps the boss-arena volume swell.
      {
        const pz = zoneAt(s.player.x);
        if (pz && pz.id !== s.lastPlayerZone) {
          const backtracking = !!(s.visitedZones && s.visitedZones[pz.id]);
          const prevZone = s.lastPlayerZone ? ZONES.find((z) => z.id === s.lastPlayerZone) : null;
          s.lastPlayerZone = pz.id;
          s.visitedZones = s.visitedZones || {};
          const isFirst = !s.visitedZones[pz.id];
          s.visitedZones[pz.id] = true;
          const icon = pz.type === "combat" ? "⚔️" : pz.type === "boss" ? "🩸" : "🟢";
          const sub  = backtracking && !isFirst ? "← BACKTRACK" : pz.type.toUpperCase();
          setBanner({ text: `${icon} ${pz.label}`, sub, ttl: 70 });
          // 🎶 BGM swap on AREA change — Area 2 uses Clockwork Ballet.
          if (!prevZone || prevZone.area !== pz.area) {
            const nextKey = pz.area === 2 ? "clockwork_ballet" : "pocket_world_battle";
            setBgmKey(nextKey);
            if (isFirst && pz.area === 2 && prevZone && prevZone.area === 1) {
              // Area 2 first-entry fanfare
              setBanner({
                text: "🏭 AREA 2 · AUTOMATED ASSEMBLY LINE",
                sub:  "CLOCKWORK BALLET",
                ttl:  140,
              });
            }
          }
          // 🎶 Boss-arena volume swell (Feb 9 pivot): +50% louder when
          // the player enters the boss zone. Reverts to base volume on
          // exit or bgm swap.
          const el = audioRef.current;
          const track = ARCADE_MUSIC[bgmKey];
          if (el && track) {
            const base = track.volume ?? 0.5;
            el.volume = muted ? 0 : (pz.type === "boss" ? Math.min(1, base * 1.5) : base);
          }
        }
      }

      // ── Player input ────────────────────────────────────────────
      const p = s.player;
      const K = s.keys, T = s.touch;
      // 🏪 Freeze player controls while the shop UI is open (react overlay).
      // Emblem timer + iframes still tick, but no movement/attacks/emblem-use.
      const inputLocked = !!s.shopOpen;
      const left  = !inputLocked && (K["a"] || K["arrowleft"]  || T.left);
      const right = !inputLocked && (K["d"] || K["arrowright"] || T.right);
      const jump  = !inputLocked && (K["w"] || K["arrowup"]    || K[" "] || T.up);
      const punch = !inputLocked && (K["j"] || T.punch);
      const kick  = !inputLocked && (K["k"] || T.kick);
      const spec  = !inputLocked && (K["l"] || T.special);
      // ✨ Signature META input — 'u' key or META touch button.
      const meta  = !inputLocked && (K["u"] || T.meta);
      // 🪙 Phoenix Emblem — press "i" (or touch USE EMBLEM) to consume one.
      // 10-second total invulnerability, cannot re-activate while active.
      const useEmblem = !inputLocked && (K["i"] || T.emblem);
      // 🏪 Strip Mall interact — press "E" (or tap 🏪) when near a shopfront.
      const interact = !inputLocked && (K["e"] || T.interact);
      if (interact && !p._interactLatch && s.stripMall && !s.shopOpen) {
        p._interactLatch = true;
        const nearShop = s.stripMall.shops.find((sh) => {
          return Math.abs(sh.x - p.x) < 60;
        });
        if (nearShop) {
          setShopOpen(nearShop);
          s.shopOpen = nearShop.id;
          setBanner({ text: `🏪 ${nearShop.name}`, sub: "WELCOME!", ttl: 60 });
        }
      }
      if (!interact) p._interactLatch = false;
      if (useEmblem && !p._emblemLatch && s.emblems > 0 && s.emblemT <= 0) {
        p._emblemLatch = true;
        s.emblems -= 1;
        s.emblemT = 600;  // 10s @ 60fps
        setBanner({ text: "⚡ INVINCIBLE! ⚡", sub: "PHOENIX EMBLEM ACTIVE", ttl: 90 });
        addParticles(p.x - s.camera, p.y - 30, "#DC2626", 20);
        addParticles(p.x - s.camera, p.y - 30, "#7C3AED", 20);
        addParticles(p.x - s.camera, p.y - 30, "#0B0F19", 12);
        s.shake = Math.max(s.shake, 10);
      }
      if (!useEmblem) p._emblemLatch = false;
      // Tick down active emblem timer
      if (s.emblemT > 0) {
        s.emblemT -= 1;
        // While active, force iframes so damagePlayer no-ops and drop
        // occasional purple/red/black sparkles for the visual pulse.
        p.iframes = Math.max(p.iframes, 3);
        if (s.t % 3 === 0) {
          const col = ["#DC2626", "#7C3AED", "#0B0F19"][s.t % 9 / 3 | 0];
          addParticles(p.x - s.camera, p.y - 20, col, 2);
        }
        // 💥 Contact damage — enemies touching the player during
        // invulnerability take heavy damage (classic Mario Star).
        s.enemies.forEach((e) => {
          if (e.hp <= 0 || e.state === "hit") return;
          const dx = Math.abs(e.x - p.x);
          const dy = Math.abs(e.y - p.y);
          if (dx < 32 && dy < 40) {
            damageEnemy(e, 999, "punch");
            addBubble("STAR K.O.!", e.x - s.camera, e.y - 60, "#F0ABFC");
          }
        });
        // Boss contact — capped tick damage (not one-shot)
        if (s.boss && s.boss.hp > 0 && s.boss.state !== "hit") {
          const bdx = Math.abs(s.boss.x - p.x);
          const bdy = Math.abs(s.boss.y - p.y);
          if (bdx < 60 && bdy < 60 && s.t % 8 === 0) {
            s.boss.hp -= 25;
            s.boss.state = "hit"; s.boss.stateT = 8;
            addBubble("BLAZE!", s.boss.x - s.camera, s.boss.y - 60, "#F0ABFC");
            addParticles(s.boss.x - s.camera, s.boss.y - 30, "#DC2626", 6);
            setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
          }
        }
      }

      if (p.iframes > 0) p.iframes -= 1;
      if (p.comboTimer > 0) { p.comboTimer -= 1; if (p.comboTimer === 0) p.combo = 0; }
      // ☕ Placebo Brew — cosmetic sparkle tick (0 gameplay effect).
      if ((s.placeboT || 0) > 0) {
        s.placeboT -= 1;
        if (s.t % 6 === 0) {
          addParticles(p.x - s.camera, p.y - 34, "#F9A8D4", 1);
        }
      }
      // 💾 Corrupted Save — brief screen-glitch FX
      if (s.glitchFX) {
        s.glitchFX.t -= 1;
        if (s.glitchFX.t <= 0) s.glitchFX = null;
      }
      // Sollor Polarity Overload buff tick
      if ((p.polarityBuffT || 0) > 0) {
        p.polarityBuffT -= 1;
        if (p.polarityBuffT === 0) {
          addBubble("STANDBY.", p.x - s.camera, p.y - 60, "#94A3B8");
        }
      }

      // ✨✨ SIGNATURE META TRAITS (Feb 2026) — per-hero unique layer.
      // Universal cooldown timers first, then per-hero behavior.
      if (p.metaCd > 0) p.metaCd -= 1;
      if (p.metaT  > 0) p.metaT  -= 1;
      if (p.flyT   > 0) p.flyT   -= 1;
      if (p.upTapT > 0) p.upTapT -= 1;
      if (p.fireT  > 0) p.fireT  -= 1;
      if (p.orbCd  > 0) p.orbCd  -= 1;
      if (p.pounceCd > 0) p.pounceCd -= 1;
      const metaEdge = meta && !p._metaLatch;
      p._metaLatch = !!meta;
      const upEdge = jump && !p._upWas;
      p._upWas = !!jump;

      // 🦉 Miko — Hoot-Dash: double-tap ↑ to float/glide for ~1.5 s.
      if (s.heroId === "miko_playable") {
        if (upEdge) {
          if (p.upTapT > 0 && p.flyT <= 0 && p.metaCd <= 0) {
            p.flyT = 90; p.metaCd = 60;
            addBubble("HOOT-DASH!", p.x - s.camera, p.y - 50, "#F59E0B");
            addParticles(p.x - s.camera, p.y - 30, "#FCD34D", 10);
          } else {
            p.upTapT = 18;   // 300ms window for the second tap
          }
        }
        if (p.flyT > 0) {
          // Slow fall + slight upward buoyancy
          p.vy = Math.min(p.vy, -0.4);
          if (s.t % 4 === 0) addParticles(p.x - s.camera, p.y - 24, "#F9A8D4", 1);
        }
      }

      // 🐈 Deanah & Muki — Muki Pounce: auto-fires every ~6s. Muki
      // darts ~200 px forward, stagger + light damage on nearest grunt.
      if (s.heroId === "deanah_muki" && p.pounceCd <= 0) {
        p.pounceCd = 360;
        const target = s.enemies
          .filter((e) => e.hp > 0 && Math.sign(e.x - p.x) === p.facing)
          .sort((a, b) => Math.abs(a.x - p.x) - Math.abs(b.x - p.x))[0];
        if (target && Math.abs(target.x - p.x) < 220) {
          damageEnemy(target, p.patk * 0.6, "punch");
          target.vx = p.facing * 6; target.vy = -3;
          addBubble("MUKI!", target.x - s.camera, target.y - 60, "#EC4899");
          addParticles(target.x - s.camera, target.y - 20, "#F9A8D4", 8);
          s.shake = Math.max(s.shake, 3);
        }
      }

      // 🦅 Roc — Wingbeat: while jump held mid-air, gravity is halved.
      // (Extra air drift only, does not fully cancel fall.)
      if (s.heroId === "roc_gym_owner" && jump && !p.onGround && p.vy > -3) {
        p.vy = Math.min(p.vy, 2.4);
        if (s.t % 5 === 0) addParticles(p.x - s.camera, p.y - 10, "#D4AF37", 1);
      }

      // 👨‍🍳 David — Sizzling Sear: every 3rd landed hit spawns a 1.5s
      // fire aura around the player; grunts touching it burn.
      if (s.heroId === "david_lauer" && p.fireT > 0) {
        if (s.t % 12 === 0) {
          addParticles(p.x - s.camera, p.y - 20, "#F97316", 3);
        }
        if (s.t % 10 === 0) {
          s.enemies.forEach((e) => {
            if (e.hp <= 0) return;
            if (Math.abs(e.x - p.x) < 50 && Math.abs(e.y - p.y) < 45) {
              damageEnemy(e, p.patk * 0.25, "punch");
              addParticles(e.x - s.camera, e.y - 20, "#F97316", 2);
            }
          });
        }
      }

      // 🕊️ Cassandra & Ledger — Ledger Anchor: META places an anchor
      // at the current position, then META again yanks Cassandra back
      // to it (short-range teleport). One anchor at a time; resets on
      // taking damage (see damagePlayer note below — MVP: persists).
      if (s.heroId === "cassandra_ledger" && metaEdge && p.metaCd <= 0) {
        if (!p.anchor) {
          p.anchor = { x: p.x };
          setBanner({ text: "🕊️ ANCHOR SET", sub: "TAP META TO YANK BACK", ttl: 60 });
          addParticles(p.x - s.camera, p.y - 30, "#2563EB", 8);
          p.metaCd = 20;
        } else {
          p.x = p.anchor.x;
          p.vx = 0;
          addBubble("YANK!", p.x - s.camera, p.y - 50, "#93C5FD");
          addParticles(p.x - s.camera, p.y - 30, "#2563EB", 14);
          p.iframes = Math.max(p.iframes, 12);
          p.anchor = null;
          p.metaCd = 90;
        }
      }

      // 👾 Glitch — Frame-Drop: META grants 36f i-frames + violet
      // digital-glitch shimmer. Cooldown ~5s.
      if (s.heroId === "glitch" && metaEdge && p.metaCd <= 0) {
        p.metaCd = 300;
        p.iframes = Math.max(p.iframes, 36);
        addBubble("FRAME-DROP!", p.x - s.camera, p.y - 50, "#A855F7");
        addParticles(p.x - s.camera, p.y - 30, "#A855F7", 14);
        addParticles(p.x - s.camera, p.y - 30, "#22C55E", 8);
        s.shake = Math.max(s.shake, 4);
      }

      // 👁️ Sollor — Orbital Watch: passive silver orb orbits the
      // player, kinetically shoving nearby grunts every 2s. No button.
      if (s.heroId === "sollor") {
        p.orbA = (p.orbA + 0.06) % (Math.PI * 2);
        if (p.orbCd <= 0) {
          p.orbCd = 120;
          s.enemies.forEach((e) => {
            if (e.hp <= 0) return;
            const dx = e.x - p.x;
            if (Math.abs(dx) < 60 && Math.abs(e.y - p.y) < 50) {
              e.vx = Math.sign(dx || 1) * 3.5;
              e.vy = -1.5;
              addParticles(e.x - s.camera, e.y - 20, "#C0C0C0", 4);
            }
          });
        }
      }

      // Horizontal
      if (p.specialActive) {
        // locked during specials
      } else if (p.state === "punch" || p.state === "kick" || p.state === "hit") {
        p.vx *= 0.6;
        p.stateT -= 1;
        if (p.stateT <= 0) p.state = "idle";
      } else {
        if (left)       { p.vx = -p.speed; p.facing = -1; p.state = "walk"; }
        else if (right) { p.vx = p.speed;  p.facing = 1;  p.state = "walk"; }
        else            { p.vx *= 0.5; if (p.onGround) p.state = "idle"; }
        // jump
        if (jump && p.onGround) { p.vy = -10; p.onGround = false; }
      }
      // gravity
      p.vy += 0.55;
      p.x  += p.vx;
      p.y  += p.vy;
      if (p.y >= GROUND_Y) { p.y = GROUND_Y; p.vy = 0; p.onGround = true; }
      p.x = clamp(p.x, 20, STAGE_WIDTH - 40);

      // Attacks (edge-triggered via cooldown)
      p._punchCd = Math.max(0, (p._punchCd || 0) - 1);
      p._kickCd  = Math.max(0, (p._kickCd  || 0) - 1);
      p._specCd  = Math.max(0, (p._specCd  || 0) - 1);

      // 🥊 Combo input buffer decay (500ms @ 60fps = 30 frames).
      // Timer starts on each attack press. If it elapses before the
      // next press, the buffer + hit-streak reset.
      if (p.bufferT > 0) {
        p.bufferT -= 1;
        if (p.bufferT === 0) { p.inputBuffer = []; p.hitStreak = 0; }
      }
      // 🥊 Finisher FX timer + freeze — while active, block movement
      // and further attacks so the animation reads clearly.
      if (s.finisherFX) {
        s.finisherFX.t -= 1;
        if (s.finisherFX.t <= 0) s.finisherFX = null;
      }

      // 🥊 registerAttackInput — called AFTER a punch/kick swing lands
      // (or whiffs). Pushes the input onto the buffer, tracks the
      // hit-streak, and fires the Combo Finisher when the buffer
      // matches a canonical string with all 3 hits connected.
      const triggerFinisher = (type) => {
        // AoE burst — wide radius, screen freeze, banner, tint.
        const isKick = type === "PPK";
        s.finisherFX = { type, t: 24 };   // ~400ms freeze/tint
        s.shake = Math.max(s.shake, 14);
        p.iframes = Math.max(p.iframes, 20);  // brief invuln during freeze
        p.superMeter = clamp(p.superMeter + 30, 0, p.superMax);
        const radius = 130;
        s.enemies.forEach((e) => {
          if (Math.abs(e.x - p.x) < radius && Math.abs(e.y - p.y) < 60) {
            damageEnemy(e, p.patk * (isKick ? 1.4 : 1.2), isKick ? "kick" : "punch");
          }
        });
        if (s.boss) {
          const dx = s.boss.x - p.x;
          if (Math.abs(dx) < radius + 30) {
            s.boss.hp -= p.patk * (isKick ? 1.4 : 1.2);
            s.boss.state = "hit"; s.boss.stateT = 12;
            addParticles(s.boss.x - s.camera, s.boss.y - 20, P.boss, 14);
            setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
          }
        }
        // 🪵 crates too — combos smash the environment
        hitBreakables(p.x + p.facing * 60, p.y, radius, 60, p.patk * (isKick ? 1.4 : 1.2));
        setBanner({
          text: isKick ? "🦵 KICK COMBO!" : "👊 PUNCH COMBO!",
          sub:  isKick ? "P · P · K FINISHER" : "P · P · P FINISHER",
          ttl:  70,
        });
        addParticles(p.x - s.camera, p.y - 30, isKick ? "#22D3EE" : "#F59E0B", 22);
        // Reset the buffer so the next press starts a fresh chain.
        p.inputBuffer = []; p.hitStreak = 0; p.bufferT = 0;
      };

      const registerAttackInput = (kind, landed) => {
        // kind: "P" | "K" ; landed: bool (did this swing hit anything?)
        p.inputBuffer.push(kind);
        if (p.inputBuffer.length > 3) p.inputBuffer.shift();
        p.hitStreak = landed ? Math.min(3, p.hitStreak + 1) : 0;
        p.bufferT = 30;   // 500ms window to input the next attack
        if (p.inputBuffer.length === 3 && p.hitStreak >= 3) {
          const seq = p.inputBuffer.join("");
          if (seq === "PPP") triggerFinisher("PPP");
          else if (seq === "PPK") triggerFinisher("PPK");
          // Non-matching sequences (e.g. KPP, PKP) — no finisher, but
          // buffer keeps rolling so the next 3-run can match.
        }
      };
      // Sollor Polarity Overload — rider on basic attacks
      const polarityActive = (p.polarityBuffT || 0) > 0;
      const fireShockwave = () => {
        // Every basic hits an AoE ring around Sollor: grunts pull IN,
        // "heavier" enemies (higher maxHp) blast OUT. Also emits a bubble.
        s.enemies.forEach((e) => {
          if (Math.abs(e.x - p.x) < 200 && Math.abs(e.y - p.y) < 80) {
            damageEnemy(e, p.matk * 0.35, "super3");
            if (e.maxHp <= 80) {
              // grunt — pull in
              e.x += (p.x - e.x) * 0.2;
              e.y = GROUND_Y - 6;
            } else {
              // heavy — knock out
              const dir = e.x > p.x ? 1 : -1;
              e.vx = dir * 9; e.vy = -3;
              e.state = "hit"; e.stateT = 25;
            }
          }
        });
        if (s.boss && Math.abs(s.boss.x - p.x) < 240) {
          s.boss.hp -= p.matk * 0.28;
          setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
        }
        addBubble("SHOCK!", p.x - s.camera, p.y - 50, "#8B5CF6");
      };

      if (punch && p._punchCd === 0 && !p.specialActive && p.state !== "hit" && !s.finisherFX) {
        p.state = "punch"; p.stateT = 18; p._punchCd = 20;
        let landed = false;
        // hit test — enemies + boss
        s.enemies.forEach((e) => {
          const dx = e.x - p.x;
          if (Math.abs(dx) < 55 && Math.sign(dx) === p.facing && Math.abs(e.y - p.y) < 40) {
            damageEnemy(e, p.patk * 0.6, "punch");
            landed = true;
          }
        });
        // 🪵 breakable-crate hit test
        hitBreakables(p.x + p.facing * 40, p.y, 55, 40, p.patk * 0.6);
        if (s.boss) {
          const dx = s.boss.x - p.x;
          if (Math.abs(dx) < 90 && Math.sign(dx) === p.facing) {
            s.boss.hp -= p.patk * 0.6;
            s.boss.state = "hit"; s.boss.stateT = 8;
            addBubble("POW!", s.boss.x - s.camera, s.boss.y - 60, P.bubble1);
            addParticles(s.boss.x - s.camera, s.boss.y - 20, P.boss, 8);
            s.player.superMeter = clamp(s.player.superMeter + 12, 0, s.player.superMax);
            setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
            landed = true;
          }
        }
        if (polarityActive) fireShockwave();
        registerAttackInput("P", landed);
      }
      if (kick && p._kickCd === 0 && !p.specialActive && p.state !== "hit" && !s.finisherFX) {
        p.state = "kick"; p.stateT = 22; p._kickCd = 26;
        let landed = false;
        s.enemies.forEach((e) => {
          const dx = e.x - p.x;
          if (Math.abs(dx) < 65 && Math.sign(dx) === p.facing && Math.abs(e.y - p.y) < 45) {
            damageEnemy(e, p.patk * 0.9, "kick");
            landed = true;
          }
        });
        // 🪵 breakable-crate hit test (kick reaches farther than punch)
        hitBreakables(p.x + p.facing * 45, p.y, 65, 45, p.patk * 0.9);
        if (s.boss) {
          const dx = s.boss.x - p.x;
          if (Math.abs(dx) < 100 && Math.sign(dx) === p.facing) {
            s.boss.hp -= p.patk * 0.9;
            addBubble("SMACK!", s.boss.x - s.camera, s.boss.y - 60, P.bubble2);
            addParticles(s.boss.x - s.camera, s.boss.y - 20, P.boss, 10);
            s.player.superMeter = clamp(s.player.superMeter + 16, 0, s.player.superMax);
            setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
            landed = true;
          }
        }
        if (polarityActive) fireShockwave();
        registerAttackInput("K", landed);
      }
      // Specials: choose the highest bar available on press
      if (spec && p._specCd === 0 && !p.specialActive) {
        const bars = Math.floor(p.superMeter / 100);
        if (bars >= 1) {
          let which = bars >= 3 ? 3 : bars >= 2 ? 2 : 1;
          p.specialActive = which;
          p.specialT = which === 3 ? 130 : which === 2 ? 60 : 45;
          p._specCd = 40;
          p.superMeter -= which * 100;
          p.iframes = p.specialT;
          const kit = s.specialsByBar[which] || {};
          setBanner({
            text: (kit.label || `BAR ${which} SPECIAL`).toUpperCase(),
            sub:  kit.category || kit.style || `${which} ★ SUPER`,
            ttl: 60,
          });
        }
      }

      // Special execution — routed per hero + bar (Feb 2026 owner-canon kits)
      if (p.specialActive) {
        p.specialT -= 1;
        const which = p.specialActive;
        const heroKey = `${s.heroId}_${which}`;
        // Small helper for bubble text pulled from the actual kit label
        const kitLabel = (s.specialsByBar[which]?.label || "SPECIAL")
          .toUpperCase()
          .replace(/[!:]/g, "")
          .split(" ")[0] + "!";

        switch (heroKey) {
          // ── DEANAH & MUKI ─────────────────────────────────────
          case "deanah_muki_1": {
            // Snap-Kick Cyclone: multi-hit AoE around player, pull grunts in
            p.vx *= 0.7;
            if (p.specialT % 6 === 0) {
              s.enemies.forEach((e) => {
                if (Math.abs(e.x - p.x) < 90 && Math.abs(e.y - p.y) < 60) {
                  damageEnemy(e, p.patk * 0.4, "super1");
                  e.x += (p.x - e.x) * 0.15;
                  e.y = GROUND_Y - 20;
                }
              });
              if (s.boss && Math.abs(s.boss.x - p.x) < 110) {
                s.boss.hp -= p.patk * 0.35;
                addBubble("CYCLONE!", s.boss.x - s.camera, s.boss.y - 80, P.bubble1);
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
            }
            break;
          }
          case "deanah_muki_2": {
            // Vortex Dash: player screams across the screen
            p.vx = p.facing * 14;
            if (p.specialT % 4 === 0) {
              s.enemies.forEach((e) => {
                if (Math.abs(e.x - p.x) < 60 && Math.abs(e.y - p.y) < 60) {
                  damageEnemy(e, p.patk * 1.2, "super2");
                }
              });
              if (s.boss && Math.abs(s.boss.x - p.x) < 80) {
                s.boss.hp -= p.patk * 0.9;
                addBubble("VORTEX!", s.boss.x - s.camera, s.boss.y - 80, P.bubble2);
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
            }
            break;
          }
          case "deanah_muki_3": {
            // Muki Meteor Drop: full-screen wipe, Muki cameo
            if (p.specialT === 100) addBubble("MUKI!", 320, 100, P.muki);
            if (p.specialT === 60)  addBubble("METEOR!", 300, 140, P.bubble3);
            if (p.specialT === 30) {
              s.enemies.forEach((e) => damageEnemy(e, 999, "super3"));
              if (s.boss) {
                s.boss.hp -= p.matk * 3.5;
                addBubble("KA-BOOM!", s.boss.x - s.camera, s.boss.y - 80, P.bubble3);
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
              s.shake = 14;
            }
            break;
          }

          // ── ROC — GYM OWNER (TANK) ────────────────────────────
          case "roc_gym_owner_1": {
            // Front Double-Bicep Stun: golden aura pulse, stagger crowd
            p.vx *= 0.4;
            if (p.specialT % 8 === 0) {
              s.enemies.forEach((e) => {
                if (Math.abs(e.x - p.x) < 110 && Math.abs(e.y - p.y) < 70) {
                  damageEnemy(e, p.patk * 0.15, "super1");
                  e.state = "hit"; e.stateT = 30;
                  e.vx = (e.x > p.x ? 1 : -1) * 3;
                  e.vy = -2;
                }
              });
              if (p.specialT === 40) addBubble("FLEX!", p.x - s.camera, p.y - 90, P.bubble3);
            }
            break;
          }
          case "roc_gym_owner_2": {
            // Protein-Beam Cannon: horizontal line beam with knockback
            p.vx *= 0.3;
            if (p.specialT % 5 === 0) {
              s.enemies.forEach((e) => {
                const dx = e.x - p.x;
                if (Math.sign(dx) === p.facing && Math.abs(dx) < 400 && Math.abs(e.y - p.y) < 50) {
                  damageEnemy(e, p.patk * 0.7, "super2");
                  e.vx = p.facing * 8;   // knockback
                  e.vy = -2;
                }
              });
              if (s.boss) {
                const dx = s.boss.x - p.x;
                if (Math.sign(dx) === p.facing && Math.abs(dx) < 500) {
                  s.boss.hp -= p.patk * 0.55;
                  addBubble("BEAM!", s.boss.x - s.camera, s.boss.y - 80, P.bubble2);
                  setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
                }
              }
            }
            break;
          }
          case "roc_gym_owner_3": {
            // Blackstonelabs Boulder Bash: Roc leaps offscreen then crashes center
            if (p.specialT === 90) addBubble("UP UP!", p.x - s.camera, 40, P.bubble3);
            if (p.specialT === 40) addBubble("KAPOW!", CANVAS_W / 2, 120, P.bubble3);
            if (p.specialT === 25) {
              s.enemies.forEach((e) => damageEnemy(e, 999, "super3"));
              if (s.boss) {
                s.boss.hp -= p.patk * 4.0;
                addBubble("HEAVYWEIGHT!", s.boss.x - s.camera, s.boss.y - 80, P.bubble3);
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
              s.shake = 18;
            }
            break;
          }

          // ── DAVID LAUER (BRUISER CHEF) ────────────────────────
          case "david_lauer_1": {
            // Sizzling Skillet Sweep: 360° low spin, trip surrounding enemies
            p.vx *= 0.5;
            if (p.specialT % 5 === 0) {
              s.enemies.forEach((e) => {
                if (Math.abs(e.x - p.x) < 85 && Math.abs(e.y - p.y) < 55) {
                  damageEnemy(e, p.patk * 0.55, "super1");
                  e.vy = -4;   // trip / pop up
                }
              });
              if (p.specialT === 40) addBubble("SIZZLE!", p.x - s.camera, p.y - 60, P.bubble4);
            }
            break;
          }
          case "david_lauer_2": {
            // Flambé Flashover: cone of flame, DoT, push to edge
            p.vx *= 0.4;
            if (p.specialT % 4 === 0) {
              s.enemies.forEach((e) => {
                const dx = e.x - p.x;
                // cone: within 200 in facing dir + vertical spread scales with dx
                if (Math.sign(dx) === p.facing && Math.abs(dx) < 220
                    && Math.abs(e.y - p.y) < 20 + Math.abs(dx) * 0.35) {
                  damageEnemy(e, p.matk * 0.35, "super2");
                  e.vx = p.facing * 6;   // pushback
                }
              });
              if (s.boss) {
                const dx = s.boss.x - p.x;
                if (Math.sign(dx) === p.facing && Math.abs(dx) < 260) {
                  s.boss.hp -= p.matk * 0.35;
                  if (p.specialT % 12 === 0) addBubble("FLAMBÉ!", s.boss.x - s.camera, s.boss.y - 80, P.bubble1);
                  setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
                }
              }
            }
            break;
          }
          case "david_lauer_3": {
            // Order Up: diner counter slides on, cookware barrage, griddle slam
            if (p.specialT === 100) addBubble("ORDER!", CANVAS_W / 2 - 40, 100, P.bubble3);
            if (p.specialT > 30 && p.specialT < 90 && p.specialT % 10 === 0) {
              addBubble("CLANG!", rand(80, CANVAS_W - 80), rand(120, 240), P.bubble2);
            }
            if (p.specialT === 25) {
              s.enemies.forEach((e) => damageEnemy(e, 999, "super3"));
              if (s.boss) {
                s.boss.hp -= p.patk * 3.8;
                addBubble("MELTDOWN!", s.boss.x - s.camera, s.boss.y - 80, P.bubble1);
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
              s.shake = 16;
            }
            break;
          }

          // ── CASSANDRA & LEDGER (TETHER DUELIST) ───────────────
          case "cassandra_ledger_1": {
            // Tactical Counter-Slide: fast low slide, i-frames + trip
            p.vx = p.facing * 10;
            if (p.specialT % 4 === 0) {
              s.enemies.forEach((e) => {
                if (Math.abs(e.x - p.x) < 70 && Math.abs(e.y - p.y) < 60) {
                  damageEnemy(e, p.patk * 0.6, "super1");
                  e.vy = -3.5;   // trip up
                }
              });
              if (s.boss && Math.abs(s.boss.x - p.x) < 90) {
                s.boss.hp -= p.patk * 0.4;
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
            }
            break;
          }
          case "cassandra_ledger_2": {
            // Overcharge Surge: bidirectional bio-electric shockwave with stun
            p.vx *= 0.2;
            if (p.specialT % 5 === 0) {
              s.enemies.forEach((e) => {
                // hits both directions
                if (Math.abs(e.x - p.x) < 320 && Math.abs(e.y - p.y) < 55) {
                  damageEnemy(e, p.patk * 0.5, "super2");
                  e.state = "hit"; e.stateT = 25;   // stun
                }
              });
              if (s.boss && Math.abs(s.boss.x - p.x) < 380) {
                s.boss.hp -= p.patk * 0.45;
                if (p.specialT % 15 === 0) addBubble("ZZZAP!", s.boss.x - s.camera, s.boss.y - 80, P.bubble2);
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
            }
            break;
          }
          case "cassandra_ledger_3": {
            // Ledger's Heavy Ordnance Drop: Ledger charges in with bazooka + dynamite
            if (p.specialT === 100) addBubble("LEDGER!", 100, 90, "#2563EB");
            if (p.specialT === 60)  addBubble("BAZOOKA!", CANVAS_W / 2, 120, P.bubble3);
            if (p.specialT > 20 && p.specialT < 50 && p.specialT % 8 === 0) {
              addBubble("BOOM!", rand(60, CANVAS_W - 60), rand(150, 260), P.bubble1);
            }
            if (p.specialT === 25) {
              s.enemies.forEach((e) => damageEnemy(e, 999, "super3"));
              if (s.boss) {
                s.boss.hp -= p.patk * 4.2;
                addBubble("ORDNANCE!", s.boss.x - s.camera, s.boss.y - 80, P.bubble3);
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
              s.shake = 18;
            }
            break;
          }

          // ── SOLLOR — ORB DUELIST (arcade-exclusive) ──────────
          case "sollor_1": {
            // Vacuum Field: heavy forward suction vortex — pulls grunts into cluster
            p.vx *= 0.3;   // Sollor plants for the pull
            const pullX = p.x + p.facing * 90;   // vortex focal point
            if (p.specialT % 5 === 0) {
              s.enemies.forEach((e) => {
                const dx = e.x - pullX;
                if (Math.abs(dx) < 260 && Math.abs(e.y - p.y) < 70) {
                  // Snap enemies inward toward the focal cluster
                  e.x += (pullX - e.x) * 0.28;
                  e.y = GROUND_Y - 10;
                  damageEnemy(e, p.matk * 0.35, "super1");
                }
              });
              if (s.boss && Math.abs(s.boss.x - pullX) < 220) {
                s.boss.hp -= p.matk * 0.3;
                if (p.specialT % 15 === 0) addBubble("VACUUM!", s.boss.x - s.camera, s.boss.y - 80, "#38BDF8");
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
            }
            break;
          }
          case "sollor_2": {
            // Pulse Repulsion: 360° red blast, frame-1 super armor, knockback all
            p.vx *= 0.15;
            p.iframes = Math.max(p.iframes, p.specialT);   // super-armor via iframes
            if (p.specialT % 5 === 0) {
              s.enemies.forEach((e) => {
                if (Math.abs(e.x - p.x) < 280 && Math.abs(e.y - p.y) < 90) {
                  damageEnemy(e, p.matk * 0.9, "super2");
                  // knockback outward
                  const dir = e.x > p.x ? 1 : -1;
                  e.vx = dir * 10;
                  e.vy = -3;
                  e.state = "hit"; e.stateT = 30;
                }
              });
              if (s.boss && Math.abs(s.boss.x - p.x) < 260) {
                s.boss.hp -= p.matk * 0.7;
                if (p.specialT % 12 === 0) addBubble("REPEL!", s.boss.x - s.camera, s.boss.y - 80, "#EF4444");
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
            }
            break;
          }
          case "sollor_3": {
            // Polarity Overload: enable the sustained buff on trigger frame
            if (p.specialT === 128) {
              p.polarityBuffT = 360;
              addBubble("OVERLOAD!", p.x - s.camera, p.y - 90, P.bubble3);
            }
            // Charging cinematic: heavy pulse at midpoint
            if (p.specialT === 60) addBubble("POLARITY!", p.x - s.camera, p.y - 60, "#8B5CF6");
            if (p.specialT === 25) {
              // Initial detonation burst that also softens the current wave
              s.enemies.forEach((e) => {
                if (Math.abs(e.x - p.x) < 220) {
                  damageEnemy(e, p.matk * 1.2, "super3");
                }
              });
              if (s.boss) {
                s.boss.hp -= p.matk * 1.8;
                addBubble("SURGE!", s.boss.x - s.camera, s.boss.y - 80, P.bubble3);
                setHud((h) => ({ ...h, bossHp: Math.max(0, s.boss.hp) }));
              }
              s.shake = 10;
            }
            break;
          }

          // ── Fallback (unknown hero) — behave like Deanah's kit
          default: {
            if (which === 1) {
              p.vx *= 0.7;
              if (p.specialT % 6 === 0) {
                s.enemies.forEach((e) => {
                  if (Math.abs(e.x - p.x) < 90 && Math.abs(e.y - p.y) < 60) {
                    damageEnemy(e, p.patk * 0.4, "super1");
                  }
                });
              }
            } else if (which === 2) {
              p.vx = p.facing * 14;
              if (p.specialT % 4 === 0) {
                s.enemies.forEach((e) => {
                  if (Math.abs(e.x - p.x) < 60 && Math.abs(e.y - p.y) < 60) {
                    damageEnemy(e, p.patk * 1.2, "super2");
                  }
                });
              }
            } else if (which === 3 && p.specialT === 30) {
              s.enemies.forEach((e) => damageEnemy(e, 999, "super3"));
              if (s.boss) s.boss.hp -= p.matk * 3.5;
              s.shake = 14;
            }
          }
        }
        // Bubble the kit label once at trigger frame for hype
        if (p.specialT === (which === 3 ? 128 : which === 2 ? 58 : 43)) {
          addBubble(kitLabel, p.x - s.camera, p.y - 70, which === 3 ? P.bubble3 : which === 2 ? P.bubble2 : P.bubble1);
        }
        if (p.specialT <= 0) { p.specialActive = null; p.state = "idle"; }
      }

      // ── Enemy update ────────────────────────────────────────────
      s.enemies.forEach((e) => {
        if (e.stateT > 0) e.stateT -= 1;
        // 🕷️ Spider drop sequence — hang, then fall, then patrol.
        if (e.kind === "spider" && e.state === "drop") {
          // Descend: web strand extends; anchor stays at ceiling.
          e.y += 2.4;
          if (e.y >= e.dropTargetY) {
            e.y = e.dropTargetY;
            e.state = "walk";
            e.stateT = 0;
          }
          return;   // no attack while dropping
        }
        if (e.state === "hit") {
          e.vx *= 0.85; e.vy += 0.6; e.x += e.vx; e.y += e.vy;
          if (e.y >= GROUND_Y) { e.y = GROUND_Y; e.vy = 0; }
          if (e.stateT <= 0) e.state = "walk";
        } else {
          const dx = p.x - e.x;
          e.facing = dx > 0 ? 1 : -1;
          const dist = Math.abs(dx);
          const isBrute  = e.kind === "brute" || e.kind === "foreman";
          const isSpider = e.kind === "spider";
          const speed = isBrute ? 1.1 : isSpider ? 1.8 : 1.5;
          if (dist > 44) {
            e.x += e.facing * speed;
            e.state = "walk";
          } else {
            e.state = "attack"; e.aiT -= 1;
            if (e.aiT <= 0) {
              e.aiT = isBrute ? 110 : isSpider ? 70 : 90;
              if (Math.abs(p.y - e.y) < 40 && p.iframes === 0) {
                damagePlayer(e.dmg || (isBrute ? 12 : isSpider ? 8 : 6));
              }
            }
          }
        }
        // 🗺️ Clamp to zone bounds — enemies don't wander into safe zones
        // or off the map (Feb 9 pivot).
        if (typeof e.zoneX0 === "number" && typeof e.zoneX1 === "number") {
          e.x = clamp(e.x, e.zoneX0, e.zoneX1);
        }
      });
      s.enemies = s.enemies.filter((e) => e.hp > 0 || e.stateT > 0);

      // Boss AI
      if (s.boss) {
        const b = s.boss;
        if (b.stateT > 0) b.stateT -= 1;
        b.aiT -= 1;
        const dx = p.x - b.x;
        b.facing = dx > 0 ? 1 : -1;
        const dist = Math.abs(dx);
        if (b.state !== "hit") {
          if (dist > 90) b.x += b.facing * 1.4;
          if (b.aiT <= 0) {
            b.aiT = 100;
            if (dist < 120 && p.iframes === 0) {
              damagePlayer(14);
            }
          }
        } else if (b.stateT <= 0) { b.state = "idle"; }
      }

      // ── Camera follow ──────────────────────────────────────────
      const targetCam = clamp(p.x - CANVAS_W / 2 + 60, 0, STAGE_WIDTH - CANVAS_W);
      s.camera += (targetCam - s.camera) * 0.12;

      // ── Pickups ────────────────────────────────────────────────
      s.pickups.forEach((pk) => {
        pk.vy += 0.4;
        pk.y  += pk.vy;
        if (pk.y >= GROUND_Y) { pk.y = GROUND_Y; pk.vy *= -0.4; }
        pk.ttl -= 1;
        if (Math.abs(pk.x - p.x) < 24 && Math.abs(pk.y - p.y) < 40) {
          s.score += 50;
          pk.ttl = 0;
          addBubble("+50", pk.x - s.camera, pk.y - 20, P.bubble3);
        }
      });
      s.pickups = s.pickups.filter((pk) => pk.ttl > 0);

      // ── 🪙 Chit drops (from breakable crates) ──────────────────
      s.drops.forEach((d) => {
        if (!d.resting) {
          d.vy += 0.5;
          d.x  += d.vx;
          d.y  += d.vy;
          if (d.y >= GROUND_Y) {
            d.y = GROUND_Y;
            if (Math.abs(d.vy) < 1.2) { d.resting = true; d.vy = 0; d.vx = 0; }
            else { d.vy *= -0.45; d.vx *= 0.65; }
          }
        }
        d.ttl -= 1;
        // Auto-magnet the coin toward the player when close
        if (d.resting && Math.abs(d.x - p.x) < 90 && Math.abs(d.y - p.y) < 50) {
          const dx = p.x - d.x;
          d.x += Math.sign(dx) * Math.min(4, Math.abs(dx));
          d.y -= 0.4;
        }
        // Pickup on contact
        if (Math.abs(d.x - p.x) < 22 && Math.abs(d.y - p.y) < 42) {
          if (d.type === "chit") {
            s.chits += 1;
            s.score += 20;
            addBubble("+1 🪙", d.x - s.camera, d.y - 20, "#FBBF24");
          } else if (d.type === "emblem") {
            s.emblems += 1;
            s.score += 500;
            addBubble("PHOENIX EMBLEM +1!", d.x - s.camera, d.y - 22, "#F0ABFC");
            addParticles(d.x - s.camera, d.y - 12, "#DC2626", 12);
            addParticles(d.x - s.camera, d.y - 12, "#7C3AED", 12);
          }
          d.ttl = 0;
        }
      });
      s.drops = s.drops.filter((d) => d.ttl > 0);

      // Bubbles + particles
      s.bubbles.forEach((b) => { b.y += b.vy; b.vy *= 0.94; b.ttl -= 1; });
      s.bubbles = s.bubbles.filter((b) => b.ttl > 0);
      s.particles.forEach((pt) => { pt.x += pt.vx; pt.y += pt.vy; pt.vy += 0.35; pt.ttl -= 1; });
      s.particles = s.particles.filter((pt) => pt.ttl > 0);

      // Sync HUD
      setHud((h) => ({
        ...h,
        score: s.score,
        lives: s.lives,
        wave:  s.wave + 1,
        superBars: Math.floor(s.player.superMeter / 100),
        ultBoostActive: s.player.ultBoostT > 0,
        playerHp: Math.max(0, s.player.hp),
        combo: s.player.combo,
        chits: s.chits,
        emblems: s.emblems,
        emblemActive: s.emblemT > 0 ? Math.ceil(s.emblemT / 60) : 0,
        placeboActive: s.placeboT > 0 ? Math.ceil(s.placeboT / 60) : 0,
        badges: s.badges || [],
        purchases: s.purchases || {},
        // ✨ Meta cooldown (0 = ready). Sollor's is passive so we
        // surface his orb-shove timer; Muki Pounce for D&M; else metaCd.
        metaCd: (s.heroId === "sollor")
          ? s.player.orbCd
          : (s.heroId === "deanah_muki")
            ? s.player.pounceCd
            : s.player.metaCd,
        metaLabel: {
          deanah_muki:      "MUKI POUNCE",
          roc_gym_owner:    "WINGBEAT",
          david_lauer:      "SIZZLING SEAR",
          cassandra_ledger: "LEDGER ANCHOR",
          sollor:           "ORBITAL WATCH",
          glitch:           "FRAME-DROP",
          miko_playable:    "HOOT-DASH",
        }[s.heroId] || "META",
      }));

      // Banner tick
      setBanner((bn) => bn.ttl > 0 ? { ...bn, ttl: bn.ttl - 1 } : bn);
    };

    // ── Render ───────────────────────────────────────────────────
    const render = () => {
      const s = stateRef.current; if (!s) return;
      const shx = s.shake ? rand(-s.shake, s.shake) : 0;
      const shy = s.shake ? rand(-s.shake, s.shake) : 0;
      // sky gradient
      const grad = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
      grad.addColorStop(0, P.skyTop);
      grad.addColorStop(1, P.skyBottom);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      ctx.save();
      ctx.translate(shx, shy);

      // Parallax: distant scrap mountains
      const cam = s.camera;
      for (let i = 0; i < 6; i++) {
        const x = ((i * 300) - (cam * 0.3) % 300) - 100;
        ctx.fillStyle = "#7C2D12";
        ctx.beginPath();
        ctx.moveTo(x, GROUND_Y);
        ctx.lineTo(x + 150, 140);
        ctx.lineTo(x + 300, GROUND_Y);
        ctx.closePath();
        ctx.fill();
        // rusty highlights
        ctx.fillStyle = "#EA580C";
        ctx.fillRect(x + 60, 200, 12, 12);
        ctx.fillRect(x + 130, 180, 10, 10);
        ctx.fillRect(x + 200, 220, 14, 14);
      }
      // mid-scrap crane silhouettes (0.6 parallax)
      for (let i = 0; i < 8; i++) {
        const x = ((i * 400) - (cam * 0.6) % 400) - 200;
        ctx.strokeStyle = "#78350F";
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.moveTo(x + 20, GROUND_Y);
        ctx.lineTo(x + 20, 120);
        ctx.lineTo(x + 120, 100);
        ctx.stroke();
      }

      // ground
      ctx.fillStyle = P.ground;
      ctx.fillRect(0, GROUND_Y + 10, CANVAS_W, CANVAS_H - GROUND_Y);
      // 🗺️ Zone-tinted ground stripes (Feb 9 pivot). Each zone paints
      // its own floor palette so the eye immediately reads combat vs
      // safe boundaries when the camera scrolls.
      for (let i = -1; i < 20; i++) {
        const worldX = i * 80 + Math.floor(cam / 80) * 80;
        const gx = worldX - cam;
        const z = zoneAt(worldX + 40);
        const pal = ZONE_FLOOR_COLORS[z?.floor] || ZONE_FLOOR_COLORS.rust;
        ctx.fillStyle = (Math.floor(worldX / 80) % 2 === 0) ? pal.a : pal.b;
        ctx.fillRect(gx, GROUND_Y + 10, 40, 6);
      }

      // 🗺️ Zone dividers — chain-link fence + arched sign at every
      // boundary (Feb 9 pivot). Rendered on the ground, behind pickups.
      ZONES.forEach((z, idx) => {
        if (idx === 0) return;   // first zone has no left divider
        const dx = z.x0 - cam;
        if (dx < -60 || dx > CANVAS_W + 60) return;
        const isBossBoundary = z.type === "boss";
        // ── Chain-link fence posts ────────────────────────────
        const postColor = isBossBoundary ? "#7C2D12" : "#4B5563";
        ctx.fillStyle = postColor;
        ctx.fillRect(dx - 2, GROUND_Y - 70, 4, 70);   // left post
        ctx.fillRect(dx + 12, GROUND_Y - 70, 4, 70);  // right post
        // wire mesh
        ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
        for (let yy = -70; yy < 0; yy += 8) {
          ctx.beginPath();
          ctx.moveTo(dx, GROUND_Y + yy);
          ctx.lineTo(dx + 14, GROUND_Y + yy);
          ctx.stroke();
        }
        for (let xx = 0; xx < 14; xx += 4) {
          ctx.beginPath();
          ctx.moveTo(dx + xx, GROUND_Y - 70);
          ctx.lineTo(dx + xx, GROUND_Y);
          ctx.stroke();
        }
        // ── Arched sign above the fence ───────────────────────
        const signColor = z.type === "safe" ? "#059669" : isBossBoundary ? "#B91C1C" : "#EA580C";
        const signW = 88;
        const signH = 16;
        const signX = dx + 7 - signW / 2;
        const signY = GROUND_Y - 90;
        ctx.fillStyle = signColor;
        ctx.fillRect(signX, signY, signW, signH);
        ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 2;
        ctx.strokeRect(signX, signY, signW, signH);
        // sign text
        ctx.fillStyle = "#FEF3C7";
        ctx.font = "bold 8px 'Press Start 2P', monospace";
        const label = z.label.length > 12 ? z.label.slice(0, 12) : z.label;
        const tw = ctx.measureText(label).width;
        ctx.fillText(label, signX + (signW - tw) / 2, signY + 12);
      });

      // scrap pile props
      for (let i = 0; i < 12; i++) {
        const x = ((i * 260) - cam) + 100;
        if (x > -60 && x < CANVAS_W + 60) {
          ctx.fillStyle = P.scrap;
          ctx.fillRect(x, GROUND_Y - 24, 40, 24);
          ctx.fillStyle = P.scrap2;
          ctx.fillRect(x + 8, GROUND_Y - 28, 16, 8);
        }
      }

      // 🏪 Strip Mall — storefronts + shopkeeper NPCs (behind pickups/enemies)
      if (s.stripMall) {
        const indoor = s.stripMall.style === "indoor";
        // Backdrop: indoor = mall tile floor + glass storefronts;
        //          outdoor = sidewalk + curb + streetlamp
        const bx = s.stripMall.x - cam;
        const bw = s.stripMall.shops.length * 220 + 200;
        if (indoor) {
          // mall tile floor
          ctx.fillStyle = "#94A3B8";
          ctx.fillRect(bx - 60, GROUND_Y, bw, 20);
          ctx.strokeStyle = "#475569";
          ctx.lineWidth = 1;
          for (let tx = bx - 60; tx < bx - 60 + bw; tx += 24) {
            ctx.beginPath();
            ctx.moveTo(tx, GROUND_Y);
            ctx.lineTo(tx, GROUND_Y + 20);
            ctx.stroke();
          }
        } else {
          // outdoor sidewalk band
          ctx.fillStyle = "#4B5563";
          ctx.fillRect(bx - 60, GROUND_Y, bw, 8);
          ctx.fillStyle = "#374151";
          ctx.fillRect(bx - 60, GROUND_Y + 8, bw, 3);
        }
        // Draw each shopfront
        s.stripMall.shops.forEach((sh) => {
          const shx = sh.x - cam;
          if (shx < -220 || shx > CANVAS_W + 220) return;
          const baseY = GROUND_Y;
          // building body
          ctx.fillStyle = indoor ? "#1E293B" : "#78350F";
          ctx.fillRect(shx - sh.w / 2, baseY - sh.h, sh.w, sh.h);
          ctx.strokeStyle = P.outline;
          ctx.lineWidth = 2;
          ctx.strokeRect(shx - sh.w / 2, baseY - sh.h, sh.w, sh.h);
          // awning (or signage backer)
          ctx.fillStyle = sh.awning;
          ctx.fillRect(shx - sh.w / 2 - 4, baseY - sh.h - 6, sh.w + 8, 22);
          // awning stripes
          ctx.fillStyle = "#FEF3C7";
          for (let stripe = 0; stripe < 8; stripe++) {
            ctx.fillRect(shx - sh.w / 2 - 4 + stripe * (sh.w + 8) / 8, baseY - sh.h - 6, 3, 22);
          }
          // sign
          ctx.fillStyle = "#0B0F19";
          ctx.font = "bold 11px 'Press Start 2P', monospace";
          const nameW = ctx.measureText(sh.name).width;
          ctx.fillText(sh.name, shx - nameW / 2, baseY - sh.h + 10);
          // door
          ctx.fillStyle = sh.door;
          ctx.fillRect(shx - 16, baseY - 50, 32, 50);
          ctx.strokeStyle = P.outline;
          ctx.strokeRect(shx - 16, baseY - 50, 32, 50);
          // door knob
          ctx.fillStyle = "#FCD34D";
          ctx.beginPath();
          ctx.arc(shx + 10, baseY - 25, 2, 0, Math.PI * 2);
          ctx.fill();
          // window
          ctx.fillStyle = indoor ? "#38BDF8" : "#F0ABFC";
          ctx.globalAlpha = 0.55;
          ctx.fillRect(shx - sh.w / 2 + 8, baseY - sh.h + 30, 40, 32);
          ctx.fillRect(shx + sh.w / 2 - 48, baseY - sh.h + 30, 40, 32);
          ctx.globalAlpha = 1;
          ctx.strokeStyle = P.outline;
          ctx.strokeRect(shx - sh.w / 2 + 8, baseY - sh.h + 30, 40, 32);
          ctx.strokeRect(shx + sh.w / 2 - 48, baseY - sh.h + 30, 40, 32);
          // Shopkeeper NPC — small stationary sprite in the doorway
          const kpX = shx;
          const kpY = baseY - 48;
          // body
          ctx.fillStyle = sh.shopkeeper_tint;
          ctx.fillRect(kpX - 8, kpY, 16, 30);
          ctx.strokeStyle = P.outline;
          ctx.lineWidth = 1.5;
          ctx.strokeRect(kpX - 8, kpY, 16, 30);
          // head
          ctx.fillStyle = "#FDE68A";
          ctx.fillRect(kpX - 6, kpY - 12, 12, 12);
          ctx.strokeRect(kpX - 6, kpY - 12, 12, 12);
          // eyes
          ctx.fillStyle = P.outline;
          ctx.fillRect(kpX - 4, kpY - 8, 2, 2);
          ctx.fillRect(kpX + 2, kpY - 8, 2, 2);
          // Interact prompt when near
          const near = Math.abs(sh.x - s.player.x) < 60;
          if (near && !s.shopOpen) {
            ctx.fillStyle = "#FCD34D";
            ctx.font = "bold 9px 'Press Start 2P', monospace";
            const prompt = "▲ PRESS [E]";
            const pw = ctx.measureText(prompt).width;
            const py = baseY - sh.h - 22 + Math.sin(s.t / 8) * 2;
            // bg pill
            ctx.fillStyle = "rgba(11,15,25,0.85)";
            ctx.fillRect(shx - pw / 2 - 4, py - 8, pw + 8, 12);
            ctx.strokeStyle = "#FCD34D";
            ctx.lineWidth = 1.5;
            ctx.strokeRect(shx - pw / 2 - 4, py - 8, pw + 8, 12);
            ctx.fillStyle = "#FCD34D";
            ctx.fillText(prompt, shx - pw / 2, py);
          }
        });
        // "→ CONTINUE" hint past the last shop, guiding player to the boss
        const lastShop = s.stripMall.shops[s.stripMall.shops.length - 1];
        const arrowX = (lastShop.x - cam) + 200;
        if (arrowX > 0 && arrowX < CANVAS_W) {
          ctx.fillStyle = "#F0ABFC";
          ctx.font = "bold 10px 'Press Start 2P', monospace";
          const t = "→ BOSS →";
          ctx.fillText(t, arrowX - ctx.measureText(t).width / 2, GROUND_Y - 20 + Math.sin(s.t / 6) * 3);
        }
      }

      // 🪵 breakable crates + 🧱 breakable walls (behind pickups + enemies)
      s.breakables.forEach((c) => {
        if (c.broken) return;
        const cx = c.x - cam;
        if (cx < -60 || cx > CANVAS_W + 60) return;

        if (c.type === "wall") {
          // stone brick wall — multi-hit, cracks progress with damage
          const dmgPct = 1 - (c.hp / c.maxHp);
          // shadow
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.beginPath();
          ctx.ellipse(cx, c.y + 3, c.w / 2 + 4, 5, 0, 0, Math.PI * 2);
          ctx.fill();
          // wall body — dark slate
          ctx.fillStyle = "#475569";
          ctx.fillRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
          ctx.strokeStyle = "#0B0F19";
          ctx.lineWidth = 2;
          ctx.strokeRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
          // brick lines
          ctx.strokeStyle = "#334155";
          ctx.lineWidth = 1;
          const rows = 5;
          for (let r = 1; r < rows; r++) {
            const ly = c.y - c.h + (c.h / rows) * r;
            ctx.beginPath();
            ctx.moveTo(cx - c.w / 2 + 1, ly); ctx.lineTo(cx + c.w / 2 - 1, ly);
            ctx.stroke();
            // vertical offset per row
            const vx = cx + (r % 2 === 0 ? -c.w / 4 : c.w / 4);
            ctx.beginPath();
            ctx.moveTo(vx, ly - c.h / rows); ctx.lineTo(vx, ly);
            ctx.stroke();
          }
          // cracks scale with damage
          if (dmgPct > 0.2) {
            ctx.strokeStyle = "#0B0F19";
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(cx - c.w / 3, c.y - c.h + 4);
            ctx.lineTo(cx - 2, c.y - c.h / 2);
            ctx.lineTo(cx + c.w / 4, c.y - 6);
            ctx.stroke();
            if (dmgPct > 0.5) {
              ctx.beginPath();
              ctx.moveTo(cx + c.w / 3, c.y - c.h + 8);
              ctx.lineTo(cx - 4, c.y - c.h / 3);
              ctx.stroke();
            }
            if (dmgPct > 0.75) {
              ctx.beginPath();
              ctx.moveTo(cx - c.w / 2 + 2, c.y - c.h / 3);
              ctx.lineTo(cx, c.y - 4);
              ctx.stroke();
            }
          }
          // small HP pips above the wall
          const pipY = c.y - c.h - 8;
          for (let i = 0; i < c.maxHp; i++) {
            ctx.fillStyle = i < c.hp ? "#F0ABFC" : "#334155";
            ctx.fillRect(cx - c.maxHp * 3 + i * 6, pipY, 4, 3);
          }
          return;
        }

        // ── oil drum ────────────────────────────────────────────────
        if (c.type === "oil_drum") {
          const dmgPct = 1 - (c.hp / c.maxHp);
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.beginPath();
          ctx.ellipse(cx, c.y + 2, c.w / 2 + 2, 4, 0, 0, Math.PI * 2);
          ctx.fill();
          // drum body — rusty red with darker band
          ctx.fillStyle = "#991B1B";
          ctx.fillRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
          ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 2;
          ctx.strokeRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
          // horizontal ribs
          ctx.strokeStyle = "#450A0A"; ctx.lineWidth = 1.5;
          for (let ry = 0.25; ry < 1; ry += 0.25) {
            ctx.beginPath();
            ctx.moveTo(cx - c.w / 2 + 1, c.y - c.h + c.h * ry);
            ctx.lineTo(cx + c.w / 2 - 1, c.y - c.h + c.h * ry);
            ctx.stroke();
          }
          // rusty highlights (streaks)
          ctx.fillStyle = "#EA580C";
          ctx.fillRect(cx - c.w / 2 + 4, c.y - c.h + 4, 3, 8);
          ctx.fillRect(cx + c.w / 2 - 6, c.y - 12, 3, 6);
          // dent for damage
          if (dmgPct > 0.4) {
            ctx.fillStyle = "#0B0F19";
            ctx.beginPath();
            ctx.ellipse(cx - c.w / 4, c.y - c.h / 2, 4, 5, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          // HP pips above
          const pipY2 = c.y - c.h - 6;
          for (let i = 0; i < c.maxHp; i++) {
            ctx.fillStyle = i < c.hp ? "#FBBF24" : "#450A0A";
            ctx.fillRect(cx - c.maxHp * 3 + i * 6, pipY2, 4, 3);
          }
          return;
        }

        // ── tire stack ──────────────────────────────────────────────
        if (c.type === "tire_stack") {
          const dmgPct = 1 - (c.hp / c.maxHp);
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.beginPath();
          ctx.ellipse(cx, c.y + 2, c.w / 2 + 3, 4, 0, 0, Math.PI * 2);
          ctx.fill();
          // Stack of 3 tires
          const tireH = c.h / 3;
          for (let ti = 0; ti < 3; ti++) {
            const ty = c.y - (ti + 1) * tireH + tireH / 2;
            // tire body
            ctx.fillStyle = "#171717";
            ctx.beginPath();
            ctx.ellipse(cx, ty, c.w / 2, tireH / 2, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 2;
            ctx.stroke();
            // inner hub
            ctx.fillStyle = "#57534E";
            ctx.beginPath();
            ctx.ellipse(cx, ty, c.w / 2 - 6, tireH / 2 - 3, 0, 0, Math.PI * 2);
            ctx.fill();
            // tread marks
            ctx.strokeStyle = "#404040"; ctx.lineWidth = 1;
            for (let sx = -c.w / 2 + 4; sx < c.w / 2 - 3; sx += 5) {
              ctx.beginPath();
              ctx.moveTo(cx + sx, ty - tireH / 2 + 1);
              ctx.lineTo(cx + sx, ty + tireH / 2 - 1);
              ctx.stroke();
            }
            // damage shear on top tire once we're 2/3 broken
            if (dmgPct > 0.66 && ti === 2) {
              ctx.strokeStyle = "#DC2626"; ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(cx - c.w / 3, ty - tireH / 2);
              ctx.lineTo(cx + c.w / 3, ty + tireH / 2);
              ctx.stroke();
            }
          }
          // HP pips
          const pipY3 = c.y - c.h - 8;
          for (let i = 0; i < c.maxHp; i++) {
            ctx.fillStyle = i < c.hp ? "#F97316" : "#404040";
            ctx.fillRect(cx - c.maxHp * 3 + i * 6, pipY3, 4, 3);
          }
          return;
        }

        // ── metal barrel (Area 2 · steel finish) ────────────────────
        if (c.type === "metal_barrel") {
          const dmgPct = 1 - (c.hp / c.maxHp);
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.beginPath();
          ctx.ellipse(cx, c.y + 2, c.w / 2 + 2, 4, 0, 0, Math.PI * 2);
          ctx.fill();
          // steel body — brushed silver with a hazard band
          ctx.fillStyle = "#94A3B8";
          ctx.fillRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
          ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 2;
          ctx.strokeRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
          // vertical rivet line
          ctx.fillStyle = "#0B0F19";
          for (let ry = 4; ry < c.h - 3; ry += 6) {
            ctx.fillRect(cx - 1, c.y - c.h + ry, 2, 2);
          }
          // yellow-black hazard band across the middle
          ctx.save();
          ctx.beginPath();
          ctx.rect(cx - c.w / 2, c.y - c.h / 2 - 3, c.w, 6);
          ctx.clip();
          ctx.fillStyle = "#000";
          ctx.fillRect(cx - c.w / 2, c.y - c.h / 2 - 3, c.w, 6);
          ctx.fillStyle = "#EAB308";
          for (let bx = cx - c.w / 2 - 6; bx < cx + c.w / 2; bx += 6) {
            ctx.beginPath();
            ctx.moveTo(bx, c.y - c.h / 2 - 3);
            ctx.lineTo(bx + 3, c.y - c.h / 2 - 3);
            ctx.lineTo(bx - 3, c.y - c.h / 2 + 3);
            ctx.lineTo(bx - 6, c.y - c.h / 2 + 3);
            ctx.closePath();
            ctx.fill();
          }
          ctx.restore();
          ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1.5;
          ctx.strokeRect(cx - c.w / 2, c.y - c.h / 2 - 3, c.w, 6);
          // dent for damage
          if (dmgPct > 0.4) {
            ctx.fillStyle = "#0B0F19";
            ctx.beginPath();
            ctx.ellipse(cx + c.w / 4, c.y - c.h / 3, 4, 5, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          // HP pips
          const pipYMB = c.y - c.h - 6;
          for (let i = 0; i < c.maxHp; i++) {
            ctx.fillStyle = i < c.hp ? "#EAB308" : "#404040";
            ctx.fillRect(cx - c.maxHp * 3 + i * 6, pipYMB, 4, 3);
          }
          return;
        }

        // ── vending machine (safe-zone reward stash) ────────────────
        if (c.type === "vending_machine") {
          const dmgPct = 1 - (c.hp / c.maxHp);
          ctx.fillStyle = "rgba(0,0,0,0.4)";
          ctx.beginPath();
          ctx.ellipse(cx, c.y + 3, c.w / 2 + 4, 5, 0, 0, Math.PI * 2);
          ctx.fill();
          // Body — red cabinet
          ctx.fillStyle = "#DC2626";
          ctx.fillRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
          ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 2;
          ctx.strokeRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
          // Glass window (cyan) with cans
          ctx.fillStyle = "#0EA5E9";
          ctx.fillRect(cx - c.w / 2 + 4, c.y - c.h + 6, c.w - 8, c.h * 0.55);
          ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1;
          ctx.strokeRect(cx - c.w / 2 + 4, c.y - c.h + 6, c.w - 8, c.h * 0.55);
          // cans inside (3 rows × 2 cols)
          ctx.fillStyle = "#F97316";
          for (let ry = 0; ry < 3; ry++) {
            for (let rx = 0; rx < 2; rx++) {
              ctx.fillRect(cx - c.w / 2 + 7 + rx * 10, c.y - c.h + 10 + ry * 10, 5, 7);
            }
          }
          // Coin slot + dispenser
          ctx.fillStyle = "#0B0F19";
          ctx.fillRect(cx - c.w / 2 + 6, c.y - c.h * 0.35, 6, 3);   // slot
          ctx.fillRect(cx - c.w / 2 + 4, c.y - 10, c.w - 8, 6);      // dispenser
          // Cracked screen if damaged
          if (dmgPct > 0.33) {
            ctx.strokeStyle = "#F1F5F9"; ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(cx - c.w / 4, c.y - c.h + 8);
            ctx.lineTo(cx, c.y - c.h + 20);
            ctx.lineTo(cx + c.w / 5, c.y - c.h + 14);
            ctx.stroke();
          }
          // HP pips
          const pipYV = c.y - c.h - 8;
          for (let i = 0; i < c.maxHp; i++) {
            ctx.fillStyle = i < c.hp ? "#F87171" : "#404040";
            ctx.fillRect(cx - c.maxHp * 3 + i * 6, pipYV, 4, 3);
          }
          return;
        }

        // ── circuit panel (low-HP, sparky) ──────────────────────────
        if (c.type === "circuit_panel") {
          ctx.fillStyle = "rgba(0,0,0,0.3)";
          ctx.beginPath();
          ctx.ellipse(cx, c.y + 2, c.w / 2 + 2, 3, 0, 0, Math.PI * 2);
          ctx.fill();
          // Green PCB body
          ctx.fillStyle = "#065F46";
          ctx.fillRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
          ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 2;
          ctx.strokeRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
          // circuit traces (gold)
          ctx.strokeStyle = "#EAB308"; ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(cx - c.w / 2 + 3, c.y - c.h + 6);
          ctx.lineTo(cx + c.w / 2 - 3, c.y - c.h + 6);
          ctx.lineTo(cx + c.w / 2 - 3, c.y - c.h * 0.6);
          ctx.moveTo(cx - c.w / 2 + 3, c.y - c.h + 12);
          ctx.lineTo(cx,                c.y - c.h + 12);
          ctx.lineTo(cx,                c.y - 4);
          ctx.stroke();
          // capacitors (little cyan dots)
          ctx.fillStyle = "#22D3EE";
          for (const [dx, dy] of [[6, 8], [-8, 14], [4, 20], [-4, 22]]) {
            ctx.beginPath();
            ctx.arc(cx + dx, c.y - c.h + dy, 1.8, 0, Math.PI * 2);
            ctx.fill();
          }
          // 1 HP so no pips required
          return;
        }

        // ── crate (default) ─────────────────────────────────────────
        // shadow
        ctx.fillStyle = "rgba(0,0,0,0.35)";
        ctx.beginPath();
        ctx.ellipse(cx, c.y + 2, c.w / 2 + 2, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        // crate body
        ctx.fillStyle = "#78350F";
        ctx.fillRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
        ctx.strokeStyle = "#0B0F19";
        ctx.lineWidth = 2;
        ctx.strokeRect(cx - c.w / 2, c.y - c.h, c.w, c.h);
        ctx.strokeStyle = "#3F2308";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx - c.w / 2 + 2, c.y - c.h / 2); ctx.lineTo(cx + c.w / 2 - 2, c.y - c.h / 2);
        ctx.moveTo(cx, c.y - c.h + 2);              ctx.lineTo(cx, c.y - 2);
        ctx.stroke();
        ctx.fillStyle = "#B45309";
        ctx.fillRect(cx - c.w / 2 + 2, c.y - c.h + 2, 3, 3);
        ctx.fillRect(cx + c.w / 2 - 5, c.y - c.h + 2, 3, 3);
        ctx.fillRect(cx - c.w / 2 + 2, c.y - 5, 3, 3);
        ctx.fillRect(cx + c.w / 2 - 5, c.y - 5, 3, 3);
      });

      // pickups (coins/dollar drops)
      s.pickups.forEach((pk) => {
        const px = pk.x - cam;
        if (px < -20 || px > CANVAS_W + 20) return;
        ctx.fillStyle = "#FDE047";
        ctx.beginPath();
        ctx.arc(px, pk.y - 4, 8 + Math.sin(s.t / 4) * 1, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = P.outline;
        ctx.font = "bold 10px 'Press Start 2P', monospace";
        ctx.fillText("$", px - 3, pk.y - 1);
      });

      // 🪙 chit drops from broken crates
      s.drops.forEach((d) => {
        const dx = d.x - cam;
        if (dx < -20 || dx > CANVAS_W + 20) return;
        // pulse alpha when despawn is close (< 90 ticks left)
        const alpha = d.ttl < 90 ? 0.4 + 0.6 * Math.abs(Math.sin(s.t / 3)) : 1;
        // shadow when airborne
        if (!d.resting) {
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.beginPath();
          ctx.ellipse(dx, GROUND_Y + 2, d.type === "emblem" ? 10 : 6, 2, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = alpha;
        if (d.type === "emblem") {
          // 🪙 Phoenix Emblem — spinning red/black/purple coin (~1.5s/rev)
          const spin = (s.t / 22) + d.x * 0.01;              // per-drop phase
          const wScale = Math.abs(Math.cos(spin));           // 0..1 (edge-on -> face)
          const coinW = 16 * (0.25 + 0.75 * wScale);         // squash on edge
          const coinH = 16;
          const cy = d.y - 10 + (d.resting ? Math.sin(s.t / 10) * 1.5 : 0);
          // rim glow
          ctx.fillStyle = "rgba(240, 171, 252, 0.35)";
          ctx.beginPath();
          ctx.ellipse(dx, cy, coinW + 4, coinH + 4, 0, 0, Math.PI * 2);
          ctx.fill();
          // coin face — three-color slice by rotation phase
          const phase = ((s.t / 6) | 0) % 3;
          const faceColor = ["#DC2626", "#7C3AED", "#0B0F19"][phase];
          const edgeColor = ["#7C3AED", "#0B0F19", "#DC2626"][phase];
          ctx.fillStyle = wScale > 0.35 ? faceColor : edgeColor;
          ctx.strokeStyle = "#F0ABFC";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.ellipse(dx, cy, coinW, coinH, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          // inner phoenix mark (simplified — flame V)
          if (wScale > 0.55) {
            ctx.strokeStyle = "#FCD34D";
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(dx - coinW * 0.4, cy + coinH * 0.35);
            ctx.lineTo(dx, cy - coinH * 0.45);
            ctx.lineTo(dx + coinW * 0.4, cy + coinH * 0.35);
            ctx.stroke();
            // gem dot
            ctx.fillStyle = "#DC2626";
            ctx.beginPath();
            ctx.arc(dx, cy - coinH * 0.15, 1.6, 0, Math.PI * 2);
            ctx.fill();
          }
          // sparkle trail while airborne
          if (!d.resting && s.t % 4 === 0) {
            addParticles(d.x - cam, d.y - 8, ["#DC2626","#7C3AED","#F0ABFC"][s.t % 3], 1);
          }
        } else {
          // regular chit coin
          ctx.fillStyle = "#FBBF24";
          ctx.beginPath();
          ctx.arc(dx, d.y - 6, 7 + Math.sin(s.t / 4 + d.x) * 0.6, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "#78350F";
          ctx.lineWidth = 2;
          ctx.stroke();
          // shine
          ctx.fillStyle = "#FEF3C7";
          ctx.beginPath();
          ctx.arc(dx - 2, d.y - 8, 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      });

      // enemies — attire dispatches by zone theme + enemy kind (Feb 9
      // pivot). Scrap Yards → grunt + brute; Automated Assembly Line →
      // droid + foreman + spider. Attire draws AFTER the base sprite so
      // it layers on top.
      s.enemies.forEach((e) => {
        const isSpider = e.kind === "spider";
        const spriteKind = isSpider ? "spider" : (e.kind === "brute" || e.kind === "foreman") ? "brute" : "grunt";
        // Spider gets its own body render (spindly legs + eye cluster)
        if (isSpider) {
          drawSpider(ctx, e, cam);
        } else {
          drawSprite(ctx, e, cam, spriteKind);
          drawEnemyAttire(ctx, e, cam);
        }
        drawHpBar(ctx, e.x - cam, e.y - (e.kind === "brute" || e.kind === "foreman" ? 58 : isSpider ? 22 : 42), e.hp / e.maxHp);
      });

      // boss
      if (s.boss) {
        drawSprite(ctx, s.boss, cam, "boss");
      }

      // player — tint by hero
      const HERO_TINT = {
        deanah_muki:       P.deanah,
        roc_gym_owner:     "#D4AF37",
        david_lauer:       "#F97316",
        cassandra_ledger:  "#2563EB",
        sollor:            "#C0C0C0",   // silver chassis
      };
      // 🪙 Phoenix Emblem active — cycle player tint R/B/P and add glow aura.
      let playerTint = HERO_TINT[s.heroId] || P.deanah;
      if (s.emblemT > 0) {
        const cyc = ((s.t / 4) | 0) % 3;
        playerTint = ["#DC2626", "#7C3AED", "#0B0F19"][cyc];
        // aura ring behind player
        const pxE = s.player.x - cam;
        const pyE = s.player.y - 20;
        for (let r = 0; r < 3; r++) {
          ctx.fillStyle = `rgba(${cyc===0?220:cyc===1?124:11},${cyc===0?38:cyc===1?58:15},${cyc===0?38:cyc===1?237:25},${0.22 - r * 0.06})`;
          ctx.beginPath();
          ctx.arc(pxE, pyE, 28 + r * 8 + Math.sin(s.t / 4) * 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      drawSprite(ctx, {
        ...s.player, tint: playerTint, size: 1.1, heroId: s.heroId,
      }, cam, s.player.specialActive ? `special${s.player.specialActive}` : (s.heroId || "deanah"));

      // Sollor — ambient floating orbs (his signature kit, always visible)
      if (s.heroId === "sollor") {
        const pxA = s.player.x - cam;
        const pyA = s.player.y - 30;
        const buff = (s.player.polarityBuffT || 0) > 0;
        // faster + wider orbit while Polarity Overload is active
        const orbitR  = buff ? 34 : 26;
        const orbitSp = buff ? 8  : 14;
        for (let o = 0; o < 2; o++) {
          const ang = s.t / orbitSp + o * Math.PI;
          const ox = pxA + Math.cos(ang) * orbitR;
          const oy = pyA - 4 + Math.sin(ang) * orbitR * 0.45;
          // outer glow
          ctx.fillStyle = buff ? "rgba(139,92,246,0.4)" : "rgba(56,189,248,0.35)";
          ctx.beginPath(); ctx.arc(ox, oy, 10, 0, Math.PI * 2); ctx.fill();
          // core orb
          ctx.fillStyle = o === 0 ? "#38BDF8" : "#F0ABFC";
          ctx.beginPath(); ctx.arc(ox, oy, 5 + Math.sin(s.t / 3 + o) * 1.2, 0, Math.PI * 2); ctx.fill();
          // highlight
          ctx.fillStyle = "rgba(255,255,255,0.7)";
          ctx.beginPath(); ctx.arc(ox - 1.5, oy - 1.5, 1.5, 0, Math.PI * 2); ctx.fill();
        }
      }

      // 🕊️ Cassandra — Ledger Anchor marker
      if (s.heroId === "cassandra_ledger" && s.player.anchor) {
        const ax = s.player.anchor.x - cam;
        const ay = GROUND_Y - 6;
        ctx.strokeStyle = "#93C5FD";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(ax, ay - 4, 8 + Math.sin(s.t / 6) * 2, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = "#2563EB";
        ctx.fillRect(ax - 2, ay - 12, 4, 12);
      }
      // 👨‍🍳 David — Sizzling Sear aura ring
      if (s.heroId === "david_lauer" && (s.player.fireT || 0) > 0) {
        const fx = s.player.x - cam;
        const fy = s.player.y - 18;
        const life = s.player.fireT / 90;
        for (let r = 0; r < 3; r++) {
          ctx.strokeStyle = `rgba(${r === 0 ? 249 : 253},${r === 0 ? 115 : 186},22,${(0.55 - r * 0.14) * life})`;
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(fx, fy, 26 + r * 6 + Math.sin(s.t / 3 + r) * 2, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      // 🦉 Miko — Hoot-Dash float glow
      if (s.heroId === "miko_playable" && (s.player.flyT || 0) > 0) {
        const mx = s.player.x - cam;
        const my = s.player.y - 22;
        ctx.fillStyle = `rgba(245,158,11,${0.28 * (s.player.flyT / 90)})`;
        ctx.beginPath(); ctx.ellipse(mx, my + 22, 24, 6, 0, 0, Math.PI * 2); ctx.fill();
      }
      // 🐈 Muki companion — tiny pounce cat trailing Deanah
      if (s.heroId === "deanah_muki") {
        const dx = s.player.x - cam - s.player.facing * 26;
        const dy = s.player.y - 8;
        const pouncing = s.player.pounceCd > 340;   // first 20f of the cd = just pounced
        ctx.fillStyle = pouncing ? "#F9A8D4" : "#EC4899";
        ctx.beginPath(); ctx.arc(dx, dy, 5, 0, Math.PI * 2); ctx.fill();
        // ears
        ctx.fillStyle = "#0F172A";
        ctx.fillRect(dx - 3, dy - 8, 2, 3);
        ctx.fillRect(dx + 1, dy - 8, 2, 3);
      }
      // 👾 Glitch — Frame-Drop shimmer while iframes are up
      if (s.heroId === "glitch" && s.player.iframes > 20 && s.player.metaCd > 260) {
        const gx = s.player.x - cam;
        const gy = s.player.y - 22;
        for (let i = 0; i < 3; i++) {
          ctx.fillStyle = `rgba(168,85,247,${0.35 - i * 0.09})`;
          ctx.fillRect(gx - 14 + i * 3, gy - 18, 28, 36);
        }
      }

      // Special FX overlays — hero-routed (Feb 2026)
      const activeSpec = s.player.specialActive;
      if (activeSpec) {
        const px = s.player.x - cam;
        const py = s.player.y - 30;
        const facing = s.player.facing;
        const heroKey = `${s.heroId}_${activeSpec}`;
        switch (heroKey) {
          // ── DEANAH & MUKI ─────────────────────────────────────
          case "deanah_muki_1": {
            // Cyclone rings
            for (let r = 0; r < 3; r++) {
              ctx.strokeStyle = r % 2 ? P.flashA : P.flashB;
              ctx.lineWidth = 4;
              const rad = 30 + r * 12 + (s.t % 20) * 1.2;
              ctx.beginPath(); ctx.arc(px, py - 2, rad, 0, Math.PI * 2); ctx.stroke();
            }
            break;
          }
          case "deanah_muki_2": {
            // Vortex streak trail
            for (let i = 0; i < 8; i++) {
              ctx.fillStyle = `rgba(240,171,252,${0.7 - i * 0.08})`;
              ctx.fillRect(px - facing * i * 12 - 12, py - 15, 24, 30);
            }
            break;
          }
          case "deanah_muki_3": {
            // Muki flies in on skateboard + meteor + flash
            ctx.fillStyle = "rgba(11,15,25,0.45)"; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            const t = 130 - s.player.specialT;
            const mx = t * 6;
            ctx.fillStyle = P.muki;             ctx.fillRect(mx, 100, 40, 30);
            ctx.fillStyle = "#0B0F19";          ctx.fillRect(mx - 4, 130, 48, 6);
            if (s.player.specialT <= 60 && s.player.specialT > 20) {
              const my = 30 + (60 - s.player.specialT) * 4;
              ctx.fillStyle = P.bubble3;
              ctx.beginPath(); ctx.arc(CANVAS_W / 2, my, 24, 0, Math.PI * 2); ctx.fill();
              ctx.fillStyle = "#F97316";
              ctx.beginPath(); ctx.arc(CANVAS_W / 2, my, 16, 0, Math.PI * 2); ctx.fill();
            }
            if (s.player.specialT <= 30) {
              ctx.fillStyle = `rgba(250,204,21,${s.player.specialT / 30})`;
              ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            }
            break;
          }

          // ── ROC — GYM OWNER ──────────────────────────────────
          case "roc_gym_owner_1": {
            // Golden flex aura pulses outward
            for (let r = 0; r < 4; r++) {
              const rad = 22 + r * 14 + (s.t % 24) * 1.6;
              ctx.strokeStyle = `rgba(212,175,55,${0.7 - r * 0.15})`;
              ctx.lineWidth = 5;
              ctx.beginPath(); ctx.arc(px, py - 2, rad, 0, Math.PI * 2); ctx.stroke();
            }
            // biceps glow diamond
            ctx.fillStyle = "rgba(250,204,21,0.55)";
            ctx.beginPath();
            ctx.moveTo(px, py - 30); ctx.lineTo(px + 26, py - 10);
            ctx.lineTo(px, py + 10);  ctx.lineTo(px - 26, py - 10);
            ctx.closePath(); ctx.fill();
            break;
          }
          case "roc_gym_owner_2": {
            // Horizontal protein beam
            const beamY = py - 4;
            ctx.fillStyle = "rgba(212,175,55,0.28)";
            ctx.fillRect(facing > 0 ? px : 0, beamY - 16, facing > 0 ? CANVAS_W - px : px, 32);
            for (let i = 0; i < 5; i++) {
              ctx.fillStyle = `rgba(255,${215 - i * 20},80,${0.8 - i * 0.13})`;
              const seg = 30 + (s.t * 4 + i * 24) % 60;
              ctx.fillRect(px + facing * (i * 40 + seg), beamY - 6 + Math.sin((s.t + i) / 3) * 3, 30, 12);
            }
            // muzzle flash
            ctx.fillStyle = "#FDE047";
            ctx.beginPath(); ctx.arc(px + facing * 14, beamY, 10 + Math.sin(s.t / 2) * 2, 0, Math.PI * 2); ctx.fill();
            break;
          }
          case "roc_gym_owner_3": {
            // Roc launches offscreen then crashes center with shockwave
            ctx.fillStyle = "rgba(11,15,25,0.5)"; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            const t = 130 - s.player.specialT;
            if (t < 40) {
              // launching up
              ctx.fillStyle = P.deanah;
              ctx.fillRect(px - 12, py - 40 - t * 6, 26, 40);
            } else if (t < 90) {
              // silhouette in sky
              ctx.fillStyle = "rgba(212,175,55,0.4)";
              ctx.beginPath(); ctx.arc(CANVAS_W / 2, 40, 24 + Math.sin(s.t/4)*3, 0, Math.PI*2); ctx.fill();
            } else {
              // crash + shockwave rings
              const crashT = t - 90;
              ctx.fillStyle = "#F97316";
              ctx.beginPath(); ctx.arc(CANVAS_W / 2, GROUND_Y - 20, 30 + crashT * 12, 0, Math.PI*2); ctx.fill();
              ctx.strokeStyle = "#FACC15"; ctx.lineWidth = 6;
              ctx.beginPath(); ctx.arc(CANVAS_W / 2, GROUND_Y - 20, 40 + crashT * 18, 0, Math.PI*2); ctx.stroke();
            }
            break;
          }

          // ── DAVID LAUER — DINER CHEF ─────────────────────────
          case "david_lauer_1": {
            // Spinning skillet arc + grease splatters
            const spinT = 45 - s.player.specialT;
            for (let i = 0; i < 6; i++) {
              const ang = (spinT * 0.3 + i * Math.PI / 3);
              const rx = px + Math.cos(ang) * 40;
              const ry = py - 6 + Math.sin(ang) * 12;
              ctx.fillStyle = "#78350F";
              ctx.fillRect(rx - 8, ry - 3, 16, 6);
              ctx.fillStyle = "#F97316";
              ctx.fillRect(rx - 4, ry - 6, 8, 3);
            }
            // grease drops
            for (let i = 0; i < 4; i++) {
              ctx.fillStyle = "rgba(250,204,21,0.7)";
              const gx = px + Math.cos(s.t / 3 + i) * 55;
              const gy = py + 4 + (s.t + i * 5) % 12;
              ctx.beginPath(); ctx.arc(gx, gy, 3, 0, Math.PI * 2); ctx.fill();
            }
            break;
          }
          case "david_lauer_2": {
            // Cone of blue flame in facing direction
            for (let i = 0; i < 12; i++) {
              const d = i * 18 + (s.t * 4) % 18;
              const spread = 6 + i * 4;
              ctx.fillStyle = `rgba(56,189,248,${0.7 - i * 0.05})`;
              ctx.beginPath();
              ctx.arc(px + facing * d, py - 4 + Math.sin((s.t + i) / 3) * 4,
                      spread + Math.sin(s.t / 2 + i) * 2, 0, Math.PI * 2);
              ctx.fill();
              ctx.fillStyle = `rgba(250,204,21,${0.5 - i * 0.035})`;
              ctx.beginPath();
              ctx.arc(px + facing * d, py - 4 + Math.cos((s.t + i) / 3) * 4,
                      spread * 0.5, 0, Math.PI * 2);
              ctx.fill();
            }
            break;
          }
          case "david_lauer_3": {
            // Diner counter slides in, cookware barrage
            ctx.fillStyle = "rgba(11,15,25,0.45)"; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            const t = 130 - s.player.specialT;
            // sliding counter
            const cx = Math.min(CANVAS_W - 200, t * 12) - 40;
            ctx.fillStyle = "#F97316";
            ctx.fillRect(cx, GROUND_Y - 60, 200, 40);
            ctx.fillStyle = "#FBBF24";
            ctx.fillRect(cx, GROUND_Y - 70, 200, 8);
            // cookware flying
            for (let i = 0; i < 5; i++) {
              const ax = ((t * 8 + i * 60) % CANVAS_W);
              const ay = 80 + Math.sin((t + i) / 4) * 40;
              ctx.fillStyle = "#78350F";
              ctx.beginPath(); ctx.arc(ax, ay, 10, 0, Math.PI * 2); ctx.fill();
              ctx.fillStyle = "#FACC15";
              ctx.fillRect(ax - 3, ay - 8, 6, 3);
            }
            if (s.player.specialT <= 25) {
              ctx.fillStyle = `rgba(249,115,22,${s.player.specialT / 25})`;
              ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            }
            break;
          }

          // ── CASSANDRA & LEDGER — TETHER DUELIST ──────────────
          case "cassandra_ledger_1": {
            // Slide dust trail behind player
            for (let i = 0; i < 8; i++) {
              ctx.fillStyle = `rgba(148,163,184,${0.6 - i * 0.07})`;
              const dx = px - facing * (i * 10);
              ctx.beginPath();
              ctx.arc(dx, GROUND_Y + 2, 6 + i * 0.6, 0, Math.PI * 2);
              ctx.fill();
            }
            // motion streaks
            ctx.strokeStyle = "rgba(37,99,235,0.7)"; ctx.lineWidth = 3;
            for (let i = 0; i < 4; i++) {
              ctx.beginPath();
              ctx.moveTo(px - facing * i * 14, py - 10 + i * 3);
              ctx.lineTo(px - facing * (i * 14 + 30), py - 10 + i * 3);
              ctx.stroke();
            }
            break;
          }
          case "cassandra_ledger_2": {
            // Bidirectional bio-electric shockwave from ground slam
            for (let dir of [-1, 1]) {
              for (let i = 0; i < 10; i++) {
                const bx = px + dir * (i * 30 + (s.t * 3) % 30);
                const bolt = Math.sin(s.t / 2 + i) * 12;
                ctx.strokeStyle = i % 2 ? "#38BDF8" : "#A78BFA";
                ctx.lineWidth = 3;
                ctx.beginPath();
                ctx.moveTo(bx, py - 10);
                ctx.lineTo(bx + dir * 10, py + bolt);
                ctx.lineTo(bx + dir * 20, py - 8);
                ctx.stroke();
              }
            }
            // ground slam impact circle
            ctx.strokeStyle = "#FACC15"; ctx.lineWidth = 5;
            ctx.beginPath(); ctx.arc(px, GROUND_Y, 30 + (s.t % 24) * 2, 0, Math.PI * 2); ctx.stroke();
            break;
          }
          case "cassandra_ledger_3": {
            // Ledger charges in with bazooka + dynamite chain explosions
            ctx.fillStyle = "rgba(11,15,25,0.5)"; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            const t = 130 - s.player.specialT;
            // Ledger sprite charging from left
            const lx = Math.min(CANVAS_W - 100, t * 10);
            ctx.fillStyle = "#2563EB";
            ctx.fillRect(lx, GROUND_Y - 60, 40, 60);
            // bazooka barrel
            ctx.fillStyle = "#111827";
            ctx.fillRect(lx + 30, GROUND_Y - 40, 40, 14);
            // muzzle flash
            if (t > 30) {
              ctx.fillStyle = "#FACC15";
              ctx.beginPath(); ctx.arc(lx + 76, GROUND_Y - 33, 10 + Math.sin(s.t / 2) * 3, 0, Math.PI * 2); ctx.fill();
            }
            // dynamite explosions
            for (let i = 0; i < 5; i++) {
              const ex = 200 + i * 60 + Math.sin(s.t / 3 + i) * 12;
              const ey = GROUND_Y - 40 - Math.abs(Math.sin((s.t + i * 8) / 6)) * 60;
              ctx.fillStyle = i % 2 ? "#F97316" : "#EF4444";
              ctx.beginPath(); ctx.arc(ex, ey, 16 + Math.sin(s.t / 2 + i) * 4, 0, Math.PI * 2); ctx.fill();
            }
            if (s.player.specialT <= 25) {
              ctx.fillStyle = `rgba(239,68,68,${s.player.specialT / 25})`;
              ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            }
            break;
          }

          // ── SOLLOR — ORB DUELIST ─────────────────────────────
          case "sollor_1": {
            // Vacuum Field: blue inward gravitational vortex in front of player
            const fx = px + facing * 90;
            const fy = py - 4;
            // outer ring pulling in
            for (let r = 0; r < 5; r++) {
              const rad = 90 - (s.t * 3 + r * 18) % 100;
              ctx.strokeStyle = `rgba(56,189,248,${0.7 - r * 0.12})`;
              ctx.lineWidth = 3 + r;
              ctx.beginPath(); ctx.arc(fx, fy, Math.max(6, rad), 0, Math.PI * 2); ctx.stroke();
            }
            // pulling streaks pointing inward
            for (let i = 0; i < 8; i++) {
              const ang = (i / 8) * Math.PI * 2 + (s.t / 12);
              const r1 = 65, r2 = 30;
              ctx.strokeStyle = "rgba(147,197,253,0.6)";
              ctx.lineWidth = 3;
              ctx.beginPath();
              ctx.moveTo(fx + Math.cos(ang) * r1, fy + Math.sin(ang) * r1);
              ctx.lineTo(fx + Math.cos(ang) * r2, fy + Math.sin(ang) * r2);
              ctx.stroke();
            }
            // center core
            ctx.fillStyle = "#38BDF8";
            ctx.beginPath(); ctx.arc(fx, fy, 8 + Math.sin(s.t / 3) * 2, 0, Math.PI * 2); ctx.fill();
            break;
          }
          case "sollor_2": {
            // Pulse Repulsion: 360° red outward blast — expanding red rings
            for (let r = 0; r < 4; r++) {
              const rad = 20 + (s.t * 6 + r * 14) % 260;
              ctx.strokeStyle = `rgba(239,68,68,${0.75 - r * 0.15})`;
              ctx.lineWidth = 6;
              ctx.beginPath(); ctx.arc(px, py - 4, rad, 0, Math.PI * 2); ctx.stroke();
            }
            // outward spike streaks
            for (let i = 0; i < 12; i++) {
              const ang = (i / 12) * Math.PI * 2;
              const r0 = 18;
              const r1 = 60 + (s.t * 6) % 40;
              ctx.strokeStyle = "#FCA5A5";
              ctx.lineWidth = 4;
              ctx.beginPath();
              ctx.moveTo(px + Math.cos(ang) * r0, py - 4 + Math.sin(ang) * r0);
              ctx.lineTo(px + Math.cos(ang) * r1, py - 4 + Math.sin(ang) * r1);
              ctx.stroke();
            }
            // super-armor chassis flash
            ctx.fillStyle = "rgba(255,255,255,0.25)";
            ctx.fillRect(px - 18, py - 40, 36, 44);
            break;
          }
          case "sollor_3": {
            // Polarity Overload — heavy prep cinematic. Buff persists after.
            ctx.fillStyle = "rgba(11,15,25,0.35)"; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            // Both orbs converge violently around player
            for (let o = 0; o < 2; o++) {
              const ang = s.t / 5 + o * Math.PI;
              const r = 46 + Math.sin(s.t / 4 + o) * 8;
              const ox = px + Math.cos(ang) * r;
              const oy = py - 20 + Math.sin(ang) * r * 0.5;
              // orb glow
              for (let k = 0; k < 3; k++) {
                ctx.fillStyle = k === 0 ? "#F0ABFC" : k === 1 ? "#38BDF8" : "#FDE047";
                ctx.beginPath(); ctx.arc(ox, oy, 14 - k * 4, 0, Math.PI * 2); ctx.fill();
              }
            }
            // rising diagnostic glitch bars behind chassis
            for (let i = 0; i < 6; i++) {
              const y = py - i * 12 - (s.t * 2) % 12;
              ctx.fillStyle = `rgba(139,92,246,${0.6 - i * 0.09})`;
              ctx.fillRect(px - 24, y, 48, 3);
            }
            if (s.player.specialT <= 25) {
              ctx.fillStyle = `rgba(139,92,246,${s.player.specialT / 25})`;
              ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            }
            break;
          }

          default: {
            // Fallback = classic pink flash
            ctx.fillStyle = "rgba(240,171,252,0.25)";
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
          }
        }
      }

      // Sollor Polarity Overload — persistent aura while buff is active
      // (renders EVEN when no special is currently being executed)
      if (s.heroId === "sollor" && (s.player.polarityBuffT || 0) > 0) {
        const px2 = s.player.x - cam;
        const py2 = s.player.y - 30;
        // subtle overclock glitch aura
        for (let i = 0; i < 3; i++) {
          const off = Math.sin(s.t / 4 + i) * 2;
          ctx.strokeStyle = i === 0 ? "rgba(139,92,246,0.55)" : i === 1 ? "rgba(56,189,248,0.5)" : "rgba(240,171,252,0.4)";
          ctx.lineWidth = 2;
          ctx.strokeRect(px2 - 20 + off, py2 - 30, 40, 44);
        }
        // orbiting orbs
        for (let o = 0; o < 2; o++) {
          const ang = s.t / 8 + o * Math.PI;
          const ox = px2 + Math.cos(ang) * 32;
          const oy = py2 + Math.sin(ang) * 14;
          ctx.fillStyle = o === 0 ? "#38BDF8" : "#F0ABFC";
          ctx.beginPath(); ctx.arc(ox, oy, 5 + Math.sin(s.t / 3) * 1.5, 0, Math.PI * 2); ctx.fill();
        }
      }

      // particles
      s.particles.forEach((pt) => {
        ctx.fillStyle = pt.color;
        ctx.fillRect(pt.x - pt.size / 2, pt.y - pt.size / 2, pt.size, pt.size);
      });

      // bubbles (cartoon)
      s.bubbles.forEach((b) => {
        const alpha = clamp(b.ttl / b.life, 0, 1);
        const scale = 1 + (1 - b.ttl / b.life) * 0.5;
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.rot);
        ctx.scale(scale, scale);
        ctx.globalAlpha = alpha;
        // starburst backdrop
        ctx.fillStyle = b.color;
        drawStarburst(ctx, 0, 0, 26, 12);
        // text
        ctx.fillStyle = P.outline;
        ctx.font = "bold 12px 'Press Start 2P', monospace";
        const w = ctx.measureText(b.text).width;
        ctx.fillText(b.text, -w / 2, 4);
        ctx.restore();
      });

      ctx.restore();

      // 🥊 Combo Finisher tint overlay — separate visual per combo:
      //   PPP → gold flash (👊 punch combo)
      //   PPK → cyan flash (🦵 kick combo)
      // Fades over the 24-frame FX window.
      if (s.finisherFX) {
        const life = s.finisherFX.t / 24;   // 1 → 0
        const alpha = 0.55 * life;
        ctx.fillStyle = s.finisherFX.type === "PPK"
          ? `rgba(34, 211, 238, ${alpha})`   // cyan
          : `rgba(245, 158, 11, ${alpha})`;  // gold
        ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
        // Thick borders reinforce the arcade-freeze feel
        ctx.fillStyle = s.finisherFX.type === "PPK" ? "#22D3EE" : "#F59E0B";
        ctx.globalAlpha = life;
        ctx.fillRect(0, 0, CANVAS_W, 6);
        ctx.fillRect(0, CANVAS_H - 6, CANVAS_W, 6);
        ctx.globalAlpha = 1;
      }

      // 💾 Corrupted Save File — 1-second digital-rain / screen-tear FX
      if (s.glitchFX) {
        // Normalize life to [0,1] over the last 60 frames so a 20s
        // Corrupted Save doesn't blow past alpha=1 for its whole run.
        const life = Math.min(1, s.glitchFX.t / 60);
        // Matrix-style green rain columns
        ctx.fillStyle = `rgba(34, 197, 94, ${0.4 * life})`;
        for (let x = 0; x < CANVAS_W; x += 8) {
          const h = 4 + ((s.t * 3 + x * 7) % 40);
          const y = ((s.t * 6 + x * 11) % CANVAS_H);
          ctx.fillRect(x, y, 2, h);
        }
        // Random horizontal tear bars
        ctx.fillStyle = `rgba(168, 85, 247, ${0.35 * life})`;
        for (let i = 0; i < 3; i++) {
          const y = ((s.t * 13 + i * 47) % CANVAS_H);
          ctx.fillRect(0, y, CANVAS_W, 2);
        }
      }

      // CRT scanlines overlay
      ctx.globalAlpha = 0.08;
      ctx.fillStyle = "#000";
      for (let y = 0; y < CANVAS_H; y += 3) {
        ctx.fillRect(0, y, CANVAS_W, 1);
      }
      ctx.globalAlpha = 1;
    };

    let last = performance.now();
    const tick = (now) => {
      const dt = now - last;
      last = now;
      // simple fixed step at ~60fps
      step();
      render();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    // Spawn wave 1 intro banner
    setBanner({ text: "AREA 1-1", sub: "THE PERIMETER GATES", ttl: 100 });

    return () => cancelAnimationFrame(raf);
  }, [ready]);

  // ── Resolve on victory/gameover ────────────────────────────────
  useEffect(() => {
    if (!result) return;
    const s = stateRef.current;
    if (!s) return;
    // report back to parent for POST /session/end
    const timeSec = Math.max(1, (performance.now() / 1000) - s.startedAtSec);
    onExit?.({
      victory: result === "victory",
      score:   s.score,
      time_sec: timeSec,
      hits_landed: s.hitsLanded,
      session_id: session.session_id,
    });
  }, [result, onExit, session]);

  // ── Touch controls ─────────────────────────────────────────────
  const btn = (label, key, color, testId) => {
    const set = (v) => { touchRef.current[key] = v; };
    return (
      <button
        data-testid={testId}
        onTouchStart={(e) => { e.preventDefault(); set(true); }}
        onTouchEnd={(e)   => { e.preventDefault(); set(false); }}
        onMouseDown={()   => set(true)}
        onMouseUp={()     => set(false)}
        onMouseLeave={()  => set(false)}
        className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-2 font-display text-lg select-none active:scale-95 transition-transform"
        style={{ borderColor: color, backgroundColor: color + "33", color }}
      >
        {label}
      </button>
    );
  };

  if (!ready) {
    return (
      <div className="text-center py-16 text-neutral-400">
        <Loader2 className="animate-spin inline mr-2" size={16} /> Booting cabinet…
      </div>
    );
  }

  return (
    <div className="text-white select-none" data-testid="miko-arcade-canvas-shell">
      {/* HUD */}
      <div className="grid grid-cols-4 gap-2 mb-2 items-center font-mono text-[10px]">
        <div className="text-left">
          <div className="text-neutral-400 text-[9px] tracking-widest uppercase">SCORE</div>
          <div className="text-[#FACC15] text-lg leading-none">{String(hud.score).padStart(6, "0")}</div>
        </div>
        <div className="text-center">
          <div className="text-neutral-400 text-[9px] tracking-widest uppercase">Area 1-{hud.wave}</div>
          <div className="text-[#38BDF8] text-sm leading-none tracking-widest">{hud.waveLabel}</div>
        </div>
        <div className="text-center" data-testid="miko-arcade-chit-count">
          <div className="text-neutral-400 text-[9px] tracking-widest uppercase">Chits</div>
          <div className="text-[#FBBF24] text-lg leading-none">🪙 × {hud.chits}</div>
        </div>
        <div className="text-right">
          <div className="text-neutral-400 text-[9px] tracking-widest uppercase">LIVES</div>
          <div className="text-[#EC4899] text-lg leading-none">{"♥".repeat(hud.lives)}</div>
        </div>
      </div>

      {/* Player HP + Super meter */}
      <div className="mb-2 grid grid-cols-2 gap-2 font-mono text-[10px]">
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-neutral-400 tracking-widest uppercase">{(session?.hero?.display || "Hero")} HP</span>
            <span className="text-[#22C55E]">{hud.playerHp}/{hud.playerMax}</span>
          </div>
          <div className="h-2 bg-neutral-900 border border-neutral-700 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#22C55E] to-[#4ADE80]" style={{ width: `${clamp((hud.playerHp / hud.playerMax) * 100, 0, 100)}%` }} />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-neutral-400 tracking-widest uppercase">
              Super Meter {hud.ultBoostActive ? "⚡" : ""}
            </span>
            <span className="text-[#FACC15]">{hud.superBars}/3 ★</span>
          </div>
          <div className="grid grid-cols-3 gap-1 h-2">
            {[1,2,3].map((i) => (
              <div
                key={i}
                className={`border ${hud.ultBoostActive ? "border-[#F0ABFC]" : "border-neutral-700"} bg-neutral-900`}
                data-testid={`miko-arcade-super-bar-${i}`}
              >
                <div
                  className="h-full"
                  style={{
                    width: hud.superBars >= i ? "100%" : "0%",
                    background: hud.ultBoostActive
                      ? "linear-gradient(90deg,#F0ABFC,#FACC15)"
                      : "#FACC15",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* ✨ META cooldown pip (Feb 2026 · per-hero signature trait) */}
      <div
        className="mb-2 flex items-center justify-between gap-2 border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-2 py-1"
        data-testid="miko-arcade-meta-pip"
      >
        <div className="flex items-center gap-1.5">
          <span className="text-[#F59E0B] text-sm">✨</span>
          <span className="text-neutral-400 text-[9px] tracking-widest uppercase">Meta</span>
          <span className="text-[#F59E0B] text-[10px] font-bold tracking-widest uppercase">
            {hud.metaLabel || "META"}
          </span>
        </div>
        <div className="flex-1 mx-2 h-1.5 bg-neutral-900 border border-neutral-700 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#F59E0B] to-[#FCD34D] transition-[width] duration-100"
            style={{
              width: `${(hud.metaCd || 0) > 0
                ? Math.max(0, 100 - Math.min(100, (hud.metaCd / 360) * 100))
                : 100}%`,
            }}
          />
        </div>
        <span className="text-[9px] font-mono uppercase tracking-widest text-neutral-500">
          {(hud.metaCd || 0) > 0 ? `${Math.ceil(hud.metaCd / 60)}s` : "READY"}
        </span>
      </div>

      {/* 🪙 Phoenix Emblem bag + active timer */}
      <div
        className="mb-2 flex items-center gap-3 border-2 px-3 py-1.5"
        style={{
          borderColor: hud.emblemActive > 0 ? "#F0ABFC" : "#7C3AED",
          background: hud.emblemActive > 0
            ? "linear-gradient(90deg, rgba(220,38,38,0.25), rgba(124,58,237,0.25), rgba(11,15,25,0.25))"
            : "rgba(11,15,25,0.6)",
        }}
        data-testid="phoenix-emblem-hud"
      >
        <PhoenixEmblemCoin active={hud.emblemActive > 0} />
        <div className="flex-1 font-mono text-[10px]">
          <div className="flex items-center justify-between">
            <span className="uppercase tracking-widest text-neutral-300">Phoenix Emblem</span>
            <span
              className="font-display text-sm"
              style={{ color: hud.emblemActive > 0 ? "#F0ABFC" : "#FCD34D" }}
              data-testid="phoenix-emblem-bag-count"
            >
              × {hud.emblems}
            </span>
          </div>
          {hud.emblemActive > 0 ? (
            <div className="mt-0.5 flex items-center gap-2">
              <span className="text-[#F0ABFC] text-[9px] uppercase tracking-widest animate-pulse">⚡ INVINCIBLE</span>
              <div className="flex-1 h-1.5 bg-neutral-900 border border-[#7C3AED]/60 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#DC2626] via-[#F0ABFC] to-[#7C3AED] transition-[width] duration-100"
                  style={{ width: `${(hud.emblemActive / 10) * 100}%` }}
                  data-testid="phoenix-emblem-active-bar"
                />
              </div>
              <span className="text-[#F0ABFC] text-[10px] font-mono tabular-nums w-6 text-right">
                {hud.emblemActive}s
              </span>
            </div>
          ) : (
            <div className="mt-0.5 text-[9px] text-neutral-500 uppercase tracking-widest">
              {hud.emblems > 0
                ? "Press [I] or tap 🪙 — 10s total invulnerability"
                : "Break crates for a rare 0.5% drop 🪙"}
            </div>
          )}
        </div>
      </div>

      {/* Canvas + cabinet chrome */}
      <div className="relative border-4 border-[#D4AF37] bg-black p-2 shadow-[0_0_24px_rgba(212,175,55,0.4)]">
        {/* BGM mute toggle (top-right of cabinet) */}
        <button
          data-testid="miko-arcade-bgm-toggle"
          onClick={toggleMute}
          className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2 py-1 border border-[#D4AF37]/60 bg-black/70 text-[10px] uppercase tracking-widest text-[#D4AF37] hover:bg-[#D4AF37]/20"
          title={muted ? "Unmute Boss Battle Breakout" : "Mute music"}
        >
          {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
          {audioBlocked ? "Play BGM" : muted ? "Music Off" : "Music On"}
        </button>
        <div className="relative overflow-hidden mx-auto" style={{ maxWidth: CANVAS_W }}>
          <canvas
            ref={canvasRef}
            data-testid="miko-arcade-canvas"
            width={CANVAS_W}
            height={CANVAS_H}
            className="block w-full h-auto"
            style={{ imageRendering: "pixelated", background: "#000" }}
          />
          {/* Banner overlay */}
          {banner.ttl > 0 && (
            <div className="absolute inset-x-0 top-1/3 pointer-events-none flex flex-col items-center">
              <div className="font-display text-3xl sm:text-5xl text-[#FACC15] drop-shadow-[3px_3px_0_#0B0F19] tracking-widest">
                {banner.text}
              </div>
              <div className="font-mono text-xs sm:text-sm text-[#38BDF8] tracking-widest mt-1">
                {banner.sub}
              </div>
            </div>
          )}
          {/* Boss HP */}
          {hud.showBoss && !result && (
            <div className="absolute inset-x-0 bottom-2 px-4">
              <div className="flex items-center justify-between text-[10px] font-mono mb-0.5">
                <span className="text-[#EF4444] uppercase tracking-widest">Junkyard Crusher 9000</span>
                <span className="text-[#EF4444]">{Math.max(0, hud.bossHp)}/{hud.bossMax}</span>
              </div>
              <div className="h-2 bg-neutral-900 border border-neutral-700">
                <div className="h-full bg-gradient-to-r from-[#EF4444] to-[#F97316]" style={{ width: `${clamp((hud.bossHp / hud.bossMax) * 100, 0, 100)}%` }} />
              </div>
            </div>
          )}
          {/* Result overlay */}
          {result && (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-3">
              <div
                className="font-display text-4xl sm:text-6xl tracking-widest"
                style={{ color: result === "victory" ? "#FACC15" : "#EF4444" }}
              >
                {result === "victory" ? "STAGE 1 CLEAR!" : "GAME OVER"}
              </div>
              <div className="font-mono text-sm text-neutral-300">
                SCORE {String(hud.score).padStart(6, "0")} · COMBO x{hud.combo}
              </div>
            </div>
          )}

          {/* 🏪 Strip Mall shop overlay */}
          {shopOpen && !result && (
            <StripMallShopOverlay
              shop={shopOpen}
              chits={hud.chits}
              emblems={hud.emblems}
              purchases={hud.purchases || {}}
              onClose={() => {
                setShopOpen(null);
                if (stateRef.current) stateRef.current.shopOpen = null;
              }}
              onBuy={(item) => {
                const s = stateRef.current;
                if (!s) return;
                const isFree  = (item.price || 0) === 0;
                const bought  = (s.purchases?.[item.id] || 0);
                if (item.limit != null && bought >= item.limit) return;
                if (!isFree && item.currency === "chits" && s.chits < item.price) return;
                if (!isFree && item.currency === "chits") s.chits -= item.price;
                s.purchases = { ...(s.purchases || {}), [item.id]: bought + 1 };
                const p = s.player;

                // ── Item effects ─────────────────────────────────
                switch (item.id) {
                  case "phoenix_emblem":
                    s.emblems += 1;
                    setBanner({ text: "🪙 PHOENIX EMBLEM", sub: "TAP 'i' TO CONSUME", ttl: 80 });
                    break;
                  case "extract":
                    p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.20);
                    setBanner({ text: "💊 EXTRACT", sub: "+20% HP", ttl: 70 });
                    break;
                  case "magnet_charm":
                    s.flags.magnet_charm = true;
                    setBanner({ text: "🧲 MAGNET CHARM", sub: "PICKUP RADIUS BOOSTED", ttl: 80 });
                    break;
                  case "salvage_kit":
                    s.flags.salvage_kit_pending = true;
                    setBanner({ text: "🛠️ SALVAGE KIT ARMED", sub: "NEXT CRATE = RARE DROP", ttl: 80 });
                    break;
                  case "bread_crust":
                    p.defBonus = (p.defBonus || 0) + 1;
                    setBanner({ text: "🥖 BREAD CRUST", sub: "+1 DEF (−5% DMG TAKEN)", ttl: 80 });
                    break;
                  case "speed_scone":
                    p.speed *= 1.10;
                    p.speedBonus = (p.speedBonus || 0) + 1;
                    setBanner({ text: "🍰 SPEED SCONE", sub: "+10% MOVE SPEED", ttl: 80 });
                    break;
                  case "power_doughnut":
                    p.patk += 5;
                    p.atkBonus = (p.atkBonus || 0) + 5;
                    setBanner({ text: "🍩 POWER DOUGHNUT", sub: "+5 BASE ATK", ttl: 80 });
                    break;
                  case "secret_scroll":
                    s.flags.combo_extension = (s.flags.combo_extension || 0) + 1;
                    setBanner({ text: "📖 SECRET SCROLL", sub: "+1 COMBO EXTENSION", ttl: 80 });
                    break;
                  case "showtime_illustrated":
                    s.flags.showtime_coin_bonus = true;
                    setBanner({ text: "⭐ SHOWTIME!", sub: "+30% COIN ON FINISHER KOs", ttl: 80 });
                    break;
                  // ── FREE joke / lore items ─────────────────────
                  case "placebo_brew":
                    s.placeboT = 1800;   // 30s @ 60fps
                    setBanner({ text: "☕ PLACEBO BREW", sub: "YOU FEEL POWERFUL.", ttl: 100 });
                    break;
                  case "fortune_cookie": {
                    const notes = [
                      "If you fall through the floor, that was a feature, not a bug.",
                      "Remember: Miko is an owl. Stop asking where her hands are.",
                      "Glitch was here first. He just never told anyone.",
                      "The Chronophage was never late. He was ALWAYS too early.",
                      "Sollor's orbs are canonically heavy. Do not lift with your back.",
                      "David's cleaver is dishwasher-safe, but nothing else is.",
                      "Cassandra's Ledger is not a metaphor. It just IS a ledger.",
                      "Roc's wingspan is measured in city blocks. That's canonical.",
                      "Deanah & Muki are one entry, not two. Do not separate them.",
                      "This message will not self-destruct. It will just get stale.",
                    ];
                    const note = notes[Math.floor(Math.random() * notes.length)];
                    setBanner({ text: "🥠 FORTUNE COOKIE", sub: note, ttl: 180 });
                    break;
                  }
                  case "unread_tos":
                    if (!s.badges.includes("i_agree")) s.badges.push("i_agree");
                    setBanner({ text: "📜 I AGREE", sub: "THE SCROLL SEALS ITSELF.", ttl: 120 });
                    break;
                  case "corrupted_save":
                    if (!s.badges.includes("system_anomaly")) s.badges.push("system_anomaly");
                    s.glitchFX = { t: 1200 };   // 20s digital-rain / screen-tear
                    s.shake = Math.max(s.shake, 20);
                    setBanner({ text: "💾 SYSTEM ANOMALY", sub: "REALITY STUTTERS FOR 20s.", ttl: 120 });
                    break;
                  // ── ⚙️ THE CONVEYOR LINE GALLERIA — Area 2 storefronts
                  case "gear_donut":
                    p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.35);
                    setBanner({ text: "⚙️ GEAR-MOLDED DONUT", sub: "+35% HP", ttl: 80 });
                    break;
                  case "conveyor_bar":
                    p.ultBoostT   = 600;               // 10s @ 60fps
                    p.superMeter  = p.superMax;        // instantly ready to 3 ★
                    setBanner({ text: "⚡ CONVEYOR BELT ENERGY BAR", sub: "3 ★ BARS LOCKED FULL — 10s", ttl: 100 });
                    break;
                  case "mushuo_fries":
                    s.flags.mushuoPassive = true;
                    s.flags.mushuoArmed   = true;
                    if (!s.badges.includes("mushuo_dust")) s.badges.push("mushuo_dust");
                    setBanner({ text: "🍟 MUSHUO DUST BOUND", sub: "SOMETHING STIRS AT HALF LIFE.", ttl: 120 });
                    break;
                  case "gear_danish":
                    setBanner({ text: "🥐 GEAR-SHAPED DANISH", sub: "GUILT-FREE. STILL BUTTERY.", ttl: 80 });
                    break;
                  case "protein_brownie":
                    p.maxHp += 10;
                    p.hp    += 10;
                    s.flags.proteinAura = true;
                    if (!s.badges.includes("protein_aura")) s.badges.push("protein_aura");
                    setBanner({ text: "🍫 PROTEIN-MOLDED BROWNIE", sub: "+10 MAX HP · MUSCLE AURA", ttl: 100 });
                    break;
                  case "mikos_blueprints":
                    s.flags.blueprintsDelivered = true;
                    if (!s.badges.includes("miko_blueprints")) s.badges.push("miko_blueprints");
                    setBanner({ text: "📜 BLUEPRINTS SECURED", sub: "DELIVER TO MIKO AFTER RUN.", ttl: 140 });
                    break;
                  case "vol_v_recoil":
                    s.flags.reviveOnKO = true;
                    if (!s.badges.includes("vol_v_recoil")) s.badges.push("vol_v_recoil");
                    setBanner({ text: "📖 VOL V: RE-COIL LEARNED", sub: "PARTY REVIVE ON NEXT WIPE.", ttl: 120 });
                    break;
                  case "ledgers_ledger":
                    s.flags.ledgersTricks = true;
                    if (!s.badges.includes("ledgers_tricks")) s.badges.push("ledgers_tricks");
                    setBanner({ text: "📕 LEDGER'S TRICK BOOK", sub: "MIX-UPS UNLOCKED.", ttl: 100 });
                    break;
                  case "irritating_digest":
                    s.flags.moveTracker = true;
                    if (!s.badges.includes("digest_reader")) s.badges.push("digest_reader");
                    setBanner({ text: "📰 IRRITATING DIGEST", sub: "MOVE COMPLETION TRACKING ONLINE.", ttl: 110 });
                    break;
                  case "zen_technique":
                    s.flags.zenPassive = true;
                    if (!s.badges.includes("zen_master")) s.badges.push("zen_master");
                    setBanner({ text: "🧘 ZEN TECHNIQUE", sub: "★ SUPER METER FILLS PASSIVELY.", ttl: 110 });
                    break;
                  case "piston_manual":
                    s.flags.kbMult = Math.max(s.flags.kbMult || 1, 1.2);
                    setBanner({ text: "🔧 PISTON PRECISION", sub: "+20% KNOCKBACK ON ATTACKS.", ttl: 100 });
                    break;
                  default:
                    setBanner({ text: item.name.toUpperCase(), sub: "ACQUIRED", ttl: 70 });
                }

                setHud((h) => ({
                  ...h,
                  chits: s.chits,
                  emblems: s.emblems,
                  purchases: { ...(s.purchases || {}) },
                  badges: [...(s.badges || [])],
                }));
              }}
            />
          )}
        </div>
      </div>

      {/* Mobile controls */}
      <div className="mt-3 grid grid-cols-2 gap-4 sm:gap-8" data-testid="miko-arcade-touch-controls">
        {/* D-pad */}
        <div className="flex flex-col items-center">
          <div className="grid grid-cols-3 gap-1 w-40 sm:w-48">
            <div />
            {btn("▲", "up",    "#38BDF8", "arcade-btn-up")}
            <div />
            {btn("◀", "left",  "#38BDF8", "arcade-btn-left")}
            <div className="w-14 h-14 sm:w-16 sm:h-16" />
            {btn("▶", "right", "#38BDF8", "arcade-btn-right")}
            <div />
            {btn("▼", "down",  "#38BDF8", "arcade-btn-down")}
            <div />
          </div>
          <div className="text-[9px] text-neutral-500 uppercase tracking-widest mt-1">
            WASD / ARROWS
          </div>
        </div>
        {/* Action buttons */}
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-3 mt-4 sm:mt-6">
            {btn("👊", "punch",    "#F472B6", "arcade-btn-punch")}
            {btn("🦵", "kick",     "#22D3EE", "arcade-btn-kick")}
            {btn("★",  "special",  "#FACC15", "arcade-btn-special")}
            {btn("✨", "meta",     "#F59E0B", "arcade-btn-meta")}
            {btn("🪙", "emblem",   "#F0ABFC", "arcade-btn-emblem")}
            {btn("🏪", "interact", "#FCD34D", "arcade-btn-interact")}
          </div>
          <div className="text-[9px] text-neutral-500 uppercase tracking-widest mt-1">
            J · K · L · U · I · E
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sprite drawing helpers ─────────────────────────────────────────
function drawSprite(ctx, o, cam, kind) {
  const x = o.x - cam;
  const y = o.y;
  const size = o.size || 1;
  const facing = o.facing || 1;
  const state = o.state || "idle";
  const tint = o.tint || "#EC4899";
  const w = 26 * size;
  const h = 44 * size;

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(facing, 1);

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.beginPath();
  ctx.ellipse(0, 4, w / 1.6, 5, 0, 0, Math.PI * 2);
  ctx.fill();

  // body
  ctx.fillStyle = tint;
  ctx.fillRect(-w / 2, -h, w, h);

  // outline
  ctx.strokeStyle = "#0B0F19";
  ctx.lineWidth = 2;
  ctx.strokeRect(-w / 2, -h, w, h);

  // head
  ctx.fillStyle = kind === "boss" ? "#78350F" : "#FDE68A";
  ctx.fillRect(-w / 2 + 3, -h - 14, w - 6, 14);
  ctx.strokeRect(-w / 2 + 3, -h - 14, w - 6, 14);

  // Hero-specific silhouette accents
  const heroId = o.heroId || (kind === "deanah" ? "deanah_muki" : null);

  // Deanah scarf accent (only Deanah/Muki)
  if (heroId === "deanah_muki") {
    ctx.fillStyle = "#F0ABFC";
    ctx.fillRect(-w / 2, -h + 4, w, 4);
  }
  // Roc — gym-owner headband (gold stripe on brow)
  if (heroId === "roc_gym_owner") {
    ctx.fillStyle = "#FACC15";
    ctx.fillRect(-w / 2 + 3, -h - 12, w - 6, 3);
    // broader shoulders
    ctx.fillStyle = tint;
    ctx.fillRect(-w / 2 - 3, -h + 2, w + 6, 6);
    ctx.strokeRect(-w / 2 - 3, -h + 2, w + 6, 6);
  }
  // David Lauer — chef hat + apron trim
  if (heroId === "david_lauer") {
    ctx.fillStyle = "#F8FAFC";           // white chef hat
    ctx.fillRect(-w / 2 + 4, -h - 22, w - 8, 8);
    ctx.strokeRect(-w / 2 + 4, -h - 22, w - 8, 8);
    ctx.fillStyle = "#F8FAFC";           // apron
    ctx.fillRect(-w / 2 + 4, -h + 8, w - 8, h - 12);
    ctx.strokeRect(-w / 2 + 4, -h + 8, w - 8, h - 12);
    ctx.fillStyle = "#78350F";           // apron belt
    ctx.fillRect(-w / 2 + 4, -h + 22, w - 8, 3);
  }
  // Cassandra & Ledger — tether cord + royal blue accent
  if (heroId === "cassandra_ledger") {
    ctx.fillStyle = "#38BDF8";
    ctx.fillRect(-w / 2, -h + 4, w, 3);
    // tether cord to Ledger (small bird companion)
    ctx.strokeStyle = "#93C5FD"; ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(w / 2 - 4, -h + 6);
    ctx.lineTo(w / 2 + 12, -h - 10);
    ctx.stroke();
  }
  // Sollor — heavy chassis + reinforced plating + diagnostic circuitry
  if (heroId === "sollor") {
    // reinforced shoulder plating
    ctx.fillStyle = "#94A3B8";
    ctx.fillRect(-w / 2 - 3, -h, w + 6, 8);
    ctx.strokeRect(-w / 2 - 3, -h, w + 6, 8);
    // diagnostic circuitry lines (glowing cyan)
    ctx.strokeStyle = "#38BDF8"; ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-w / 2 + 4, -h + 12); ctx.lineTo(-w / 2 + 4, -h + 26);
    ctx.moveTo( w / 2 - 4, -h + 12); ctx.lineTo( w / 2 - 4, -h + 26);
    ctx.moveTo(-w / 2 + 4, -h + 26); ctx.lineTo( w / 2 - 4, -h + 26);
    ctx.stroke();
    // status LED
    ctx.fillStyle = "#F0ABFC";
    ctx.fillRect(-2, -h + 20, 4, 4);
  }

  // eye
  ctx.fillStyle = "#0B0F19";
  ctx.fillRect(w / 4 - 2, -h - 8, 3, 3);

  // Muki pet on Deanah's shoulder (bar 1&2) — only for Deanah
  if (heroId === "deanah_muki" && (kind === "deanah_muki" || kind === "deanah" || kind === "special1" || kind === "special2")) {
    ctx.fillStyle = P.muki;
    ctx.fillRect(w / 2 - 6, -h - 6, 8, 8);
    ctx.strokeRect(w / 2 - 6, -h - 6, 8, 8);
  }
  // Ledger perch (small companion bird) on Cassandra's shoulder — bars 1&2
  if (heroId === "cassandra_ledger" && (kind === "cassandra_ledger" || kind === "special1" || kind === "special2")) {
    ctx.fillStyle = "#2563EB";
    ctx.fillRect(w / 2 - 4, -h - 12, 10, 8);
    ctx.strokeRect(w / 2 - 4, -h - 12, 10, 8);
    // beak
    ctx.fillStyle = "#FACC15";
    ctx.fillRect(w / 2 + 6, -h - 9, 3, 2);
  }

  // action pose
  if (state === "punch") {
    ctx.fillStyle = tint;
    ctx.fillRect(w / 2 - 2, -h + 14, 14 * size, 8);
    ctx.strokeRect(w / 2 - 2, -h + 14, 14 * size, 8);
  } else if (state === "kick") {
    ctx.fillStyle = tint;
    ctx.fillRect(w / 2 - 2, -8, 18 * size, 8);
    ctx.strokeRect(w / 2 - 2, -8, 18 * size, 8);
  } else if (state === "hit") {
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.fillRect(-w / 2, -h, w, h);
  }

  // boss claw hand
  if (kind === "boss") {
    ctx.fillStyle = "#DC2626";
    ctx.fillRect(w / 2 - 4, -h + 8, 16, 12);
    ctx.strokeRect(w / 2 - 4, -h + 8, 16, 12);
    // spikes
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = "#111";
      ctx.fillRect(w / 2 + 4 + i * 4, -h + 20, 3, 6);
    }
  }

  ctx.restore();

  // i-frame flash
  if (o.iframes > 0 && Math.floor(o.iframes / 4) % 2) {
    ctx.save();
    ctx.globalAlpha = 0.6;
    ctx.fillStyle = "#fff";
    ctx.fillRect(x - w / 2, y - h - 14, w, h + 14);
    ctx.restore();
  }
}

// 🗑️🏭 Enemy attire overlay — theme-aware (Feb 9, 2026).
// • Scrap Yards (Area 1) → grunts get grease goggles + wrench, brutes
//   get rusty helm + spiked pads + pipe wrench.
// • Automated Assembly Line (Area 2) → droids get chrome helm + visor
//   + arc-welder, foreman bots get spinning saw arm + hazard-stripe
//   shoulder pads.
// Owner directive: every enemy dresses to the zone it patrols.
function drawEnemyAttire(ctx, o, cam) {
  const zone = zoneAt(o.x);
  if (zone?.theme === "factory") {
    drawFactoryAttire(ctx, o, cam);
  } else {
    drawScrapYardAttire(ctx, o, cam);
  }
}

function drawScrapYardAttire(ctx, o, cam) {
  const x = o.x - cam;
  const y = o.y;
  const size = o.size || 1;
  const facing = o.facing || 1;
  const w = 26 * size;
  const h = 44 * size;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(facing, 1);

  if (o.kind === "brute") {
    // Rusty helm (dome) — dark iron with a horn ridge
    ctx.fillStyle = "#4B5563";
    ctx.beginPath();
    ctx.arc(0, -h - 4, w / 2 - 2, Math.PI, 0);
    ctx.fill();
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1.5;
    ctx.stroke();
    // horn ridge (rusty orange bolts)
    ctx.fillStyle = "#EA580C";
    ctx.fillRect(-2, -h - 12, 4, 4);
    // Spiked shoulder pads
    ctx.fillStyle = "#374151";
    ctx.fillRect(-w / 2 - 4, -h + 2, w + 8, 8);
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1.5;
    ctx.strokeRect(-w / 2 - 4, -h + 2, w + 8, 8);
    // spikes on the pad
    ctx.fillStyle = "#9CA3AF";
    for (let sx = -w / 2; sx < w / 2 + 4; sx += 6) {
      ctx.beginPath();
      ctx.moveTo(sx, -h + 2);
      ctx.lineTo(sx + 3, -h - 3);
      ctx.lineTo(sx + 6, -h + 2);
      ctx.closePath();
      ctx.fill();
    }
    // Massive pipe wrench in the front hand
    ctx.fillStyle = "#57534E";
    ctx.fillRect(w / 2 - 2, -h + 18, 24, 5);
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1;
    ctx.strokeRect(w / 2 - 2, -h + 18, 24, 5);
    ctx.fillStyle = "#EAB308";
    ctx.fillRect(w / 2 + 18, -h + 15, 6, 11);   // wrench jaw
    ctx.strokeRect(w / 2 + 18, -h + 15, 6, 11);
  } else {
    // ── Grunt attire ────────────────────────────────────────
    // Grease-goggles across the eyes
    ctx.fillStyle = "#111827";
    ctx.fillRect(-w / 2 + 3, -h - 8, w - 6, 4);
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1;
    ctx.strokeRect(-w / 2 + 3, -h - 8, w - 6, 4);
    // goggle lenses (light glint)
    ctx.fillStyle = "#F0ABFC";
    ctx.fillRect(-w / 2 + 5, -h - 7, 3, 2);
    ctx.fillRect(w / 2 - 8,  -h - 7, 3, 2);
    // Oil-stain on chest (dark blob)
    ctx.fillStyle = "rgba(11, 15, 25, 0.5)";
    ctx.beginPath();
    ctx.ellipse(-3, -h + 22, 6, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    // Wrench in the front hand
    ctx.fillStyle = "#94A3B8";
    ctx.fillRect(w / 2 - 2, -h + 22, 12, 3);
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1;
    ctx.strokeRect(w / 2 - 2, -h + 22, 12, 3);
    // wrench jaw
    ctx.fillStyle = "#CBD5E1";
    ctx.fillRect(w / 2 + 8, -h + 20, 4, 6);
    ctx.strokeRect(w / 2 + 8, -h + 20, 4, 6);
    // Small "SCRAP" tag on the belt
    ctx.fillStyle = "#DC2626";
    ctx.fillRect(-3, -h + 30, 6, 3);
  }
  ctx.restore();
}


// 🏭 Automated Assembly Line attire (Feb 9, 2026) — Line Worker Droids
// get chrome helm + single-eye visor + arc-welder; Foreman Bots get
// hazard-stripe pads + spinning saw arm.
function drawFactoryAttire(ctx, o, cam) {
  const x = o.x - cam;
  const y = o.y;
  const size = o.size || 1;
  const facing = o.facing || 1;
  const w = 26 * size;
  const h = 44 * size;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(facing, 1);

  if (o.kind === "foreman") {
    // Chrome dome helm with center bolt
    ctx.fillStyle = "#94A3B8";
    ctx.beginPath();
    ctx.arc(0, -h - 4, w / 2 - 2, Math.PI, 0);
    ctx.fill();
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1.5;
    ctx.stroke();
    // center bolt (red hazard)
    ctx.fillStyle = "#DC2626";
    ctx.beginPath();
    ctx.arc(0, -h - 8, 3, 0, Math.PI * 2);
    ctx.fill();
    // Hazard-stripe shoulder pads (yellow + black diagonals)
    ctx.save();
    ctx.beginPath();
    ctx.rect(-w / 2 - 4, -h + 2, w + 8, 8);
    ctx.clip();
    ctx.fillStyle = "#000";
    ctx.fillRect(-w / 2 - 4, -h + 2, w + 8, 8);
    ctx.fillStyle = "#EAB308";
    for (let sx = -w / 2 - 4; sx < w / 2 + 4; sx += 6) {
      ctx.beginPath();
      ctx.moveTo(sx, -h + 2);
      ctx.lineTo(sx + 3, -h + 2);
      ctx.lineTo(sx - 3, -h + 10);
      ctx.lineTo(sx - 6, -h + 10);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1.5;
    ctx.strokeRect(-w / 2 - 4, -h + 2, w + 8, 8);
    // Spinning saw arm on the front side
    ctx.fillStyle = "#57534E";
    ctx.fillRect(w / 2 - 2, -h + 18, 16, 4);
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1;
    ctx.strokeRect(w / 2 - 2, -h + 18, 16, 4);
    // saw blade
    ctx.fillStyle = "#F1F5F9";
    const sawR = 8;
    const sawX = w / 2 + 16;
    const sawY = -h + 20;
    ctx.beginPath();
    ctx.arc(sawX, sawY, sawR, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1;
    ctx.stroke();
    // teeth
    ctx.fillStyle = "#0B0F19";
    for (let a = 0; a < 8; a++) {
      const ang = (a / 8) * Math.PI * 2;
      const tx = sawX + Math.cos(ang) * sawR;
      const ty = sawY + Math.sin(ang) * sawR;
      ctx.beginPath();
      ctx.arc(tx, ty, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
    // center hub
    ctx.fillStyle = "#DC2626";
    ctx.beginPath();
    ctx.arc(sawX, sawY, 2, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // ── Line Worker Droid ─────────────────────────────────
    // Chrome bucket-helmet
    ctx.fillStyle = "#CBD5E1";
    ctx.fillRect(-w / 2 + 1, -h - 10, w - 2, 8);
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1.5;
    ctx.strokeRect(-w / 2 + 1, -h - 10, w - 2, 8);
    // Glowing cyclops visor (single-eye slit)
    ctx.fillStyle = "#F87171";
    ctx.fillRect(-w / 2 + 4, -h - 6, w - 8, 2);
    // helm antenna
    ctx.strokeStyle = "#94A3B8"; ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, -h - 10); ctx.lineTo(0, -h - 15);
    ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.beginPath();
    ctx.arc(0, -h - 16, 1.5, 0, Math.PI * 2);
    ctx.fill();
    // Rivet chest plate (silver square with 4 bolts)
    ctx.fillStyle = "#94A3B8";
    ctx.fillRect(-w / 2 + 4, -h + 12, w - 8, 12);
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1;
    ctx.strokeRect(-w / 2 + 4, -h + 12, w - 8, 12);
    ctx.fillStyle = "#0B0F19";
    ctx.fillRect(-w / 2 + 6, -h + 14, 2, 2);
    ctx.fillRect(w / 2 - 8,  -h + 14, 2, 2);
    ctx.fillRect(-w / 2 + 6, -h + 20, 2, 2);
    ctx.fillRect(w / 2 - 8,  -h + 20, 2, 2);
    // Arc welder in the front hand (stubby dark tool with cyan tip)
    ctx.fillStyle = "#374151";
    ctx.fillRect(w / 2 - 2, -h + 24, 10, 4);
    ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1;
    ctx.strokeRect(w / 2 - 2, -h + 24, 10, 4);
    // arc-tip glow
    ctx.fillStyle = "#22D3EE";
    ctx.beginPath();
    ctx.arc(w / 2 + 8, -h + 26, 2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}


// 🕷️ Conveyor Spider — spindly-legged critter that drops from the
// ceiling on a web. Feb 9, 2026 (Area 2 owner directive).
function drawSpider(ctx, o, cam) {
  const x = o.x - cam;
  const y = o.y;
  const dropping = o.state === "drop";
  // web strand from the ceiling anchor down to the spider
  if (dropping || y < GROUND_Y - 2) {
    ctx.strokeStyle = "#F1F5F9"; ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, o.webAnchorY || 0);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  // shadow (only once on ground)
  if (!dropping) {
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.beginPath();
    ctx.ellipse(x, GROUND_Y + 4, 14, 3, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  // legs (8) — spider has 8 spindly legs, 4 per side
  ctx.strokeStyle = P.spider; ctx.lineWidth = 1.5;
  for (let li = -3; li <= 3; li += 2) {
    ctx.beginPath();
    ctx.moveTo(x, y - 8);
    ctx.lineTo(x + li * 3, y - 2);
    ctx.lineTo(x + li * 4, y);
    ctx.stroke();
  }
  // body — dark oval
  ctx.fillStyle = P.spider;
  ctx.beginPath();
  ctx.ellipse(x, y - 8, 8, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#0B0F19"; ctx.lineWidth = 1;
  ctx.stroke();
  // 6 red eye cluster
  ctx.fillStyle = "#F87171";
  for (let ei = -2; ei <= 2; ei += 2) {
    ctx.beginPath();
    ctx.arc(x + ei, y - 10, 1, 0, Math.PI * 2);
    ctx.fill();
  }
}



function drawHpBar(ctx, x, y, pct) {  const w = 30;
  ctx.fillStyle = "#111";
  ctx.fillRect(x - w / 2, y, w, 4);
  ctx.fillStyle = pct > 0.4 ? "#22C55E" : "#EF4444";
  ctx.fillRect(x - w / 2, y, w * clamp(pct, 0, 1), 4);
}

function drawStarburst(ctx, cx, cy, r, points) {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const ang = (i / (points * 2)) * Math.PI * 2;
    const rad = i % 2 === 0 ? r : r * 0.55;
    const x = cx + Math.cos(ang) * rad;
    const y = cy + Math.sin(ang) * rad;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "#0B0F19";
  ctx.lineWidth = 2;
  ctx.stroke();
}
